import React, { useState, useEffect, useRef, useCallback } from "react";

// ─── Mobile Compatibility Helpers ─────────────────────────────────────────────
// Android WebView can suppress window.confirm dialogs when the page is loaded
// from a file:// or non-standard origin. This wrapper ensures it always works.
function mobileConfirm(message) {
  try {
    // Standard browser confirm — works in Capacitor's https://localhost context
    const result = window.confirm(message);
    return result;
  } catch {
    // Fallback: treat as confirmed (should never hit in Capacitor)
    return true;
  }
}

// navigator.clipboard requires a secure context (https) and user gesture.
// Capacitor uses https://localhost so this works, but we add a textarea fallback
// for edge cases (very old Android WebView versions < 67).
async function mobileClipboardWrite(text) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  // Fallback for Android WebView < 67
  const el = document.createElement("textarea");
  el.value = text;
  el.style.position = "fixed";
  el.style.opacity = "0";
  document.body.appendChild(el);
  el.focus();
  el.select();
  document.execCommand("copy");
  document.body.removeChild(el);
}

// ─── Theme ────────────────────────────────────────────────────────────────────
function makeTheme(dark) {
  return dark ? {
    bg: "#0a0a0a", bgElevated: "#0f0f0f", bgModal: "#111", bgInput: "#0d0d0d",
    border: "#1a1a1a", borderMid: "#222", borderHi: "#2a2a2a",
    text: "#e8e0d0", textMuted: "#c8c0b0", textDim: "#888", textFaint: "#555", textGhost: "#444", textDeep: "#333",
    accent: "#f0e8d8", accentText: "#0a0a0a",
    headerBg: "linear-gradient(180deg,#080808,#060606)",
    warnBg: "#110d08", warnBorder: "#2a1f10", warnText: "#a07040", isDark: true,
  } : {
    bg: "#f5f2ee", bgElevated: "#ffffff", bgModal: "#ffffff", bgInput: "#faf8f5",
    border: "#e0dbd4", borderMid: "#d4cfc8", borderHi: "#c8c2ba",
    text: "#1a1612", textMuted: "#3d3830", textDim: "#6b6358", textFaint: "#8a8078", textGhost: "#a09890", textDeep: "#b8b0a8",
    accent: "#1a1612", accentText: "#f5f2ee",
    headerBg: "linear-gradient(180deg,#e4e0da,#ebe7e2)",
    warnBg: "#fff8f0", warnBorder: "#f0d8b8", warnText: "#a06020", isDark: false,
  };
}

// ─── Models ───────────────────────────────────────────────────────────────────
const MODELS = [
  {
    id: "claude", label: "Claude", color: "#D97B4A", icon: "◆",
    docsUrl: "https://console.anthropic.com/settings/keys", docsLabel: "console.anthropic.com",
    keyHint: "Starts with sk-ant-...", free: true, freeNote: "Free tier available",
    versions: [
      { id: "claude-opus-4-6",             label: "Claude Opus 4.6",      note: "Most powerful" },
      { id: "claude-sonnet-4-6",           label: "Claude Sonnet 4.6",    note: "Recommended" },
      { id: "claude-haiku-4-5-20251001",   label: "Claude Haiku 4.5",     note: "Fast & cheap" },
    ],
    defaultVersion: "claude-sonnet-4-6",
  },
  {
    id: "chatgpt", label: "ChatGPT", color: "#19C37D", icon: "●",
    docsUrl: "https://platform.openai.com/api-keys", docsLabel: "platform.openai.com",
    keyHint: "Starts with sk-...", free: false, freeNote: "Requires paid plan",
    versions: [
      { id: "gpt-4o",       label: "GPT-4o",       note: "Recommended" },
      { id: "gpt-4o-mini",  label: "GPT-4o Mini",  note: "Faster & cheaper" },
      { id: "gpt-4-turbo",  label: "GPT-4 Turbo",  note: "Previous gen" },
    ],
    defaultVersion: "gpt-4o",
  },
  {
    id: "gemini", label: "Gemini", color: "#4A8FD9", icon: "✦",
    docsUrl: "https://aistudio.google.com/app/apikey", docsLabel: "aistudio.google.com",
    keyHint: "Starts with AIza...", free: true, freeNote: "Free tier available",
    versions: [
      { id: "gemini-2.5-flash",      label: "Gemini 2.5 Flash",      note: "Recommended" },
      { id: "gemini-2.5-pro",        label: "Gemini 2.5 Pro",        note: "Most capable" },
      { id: "gemini-2.5-flash-lite", label: "Gemini 2.5 Flash-Lite", note: "Fastest" },
    ],
    defaultVersion: "gemini-2.5-flash",
  },
];

function getDefaultVersions() {
  return Object.fromEntries(MODELS.map((m) => [m.id, m.defaultVersion]));
}

// ─── Persona Modes (Feature 4) ────────────────────────────────────────────────
const PERSONA_MODES = [
  {
    id: "balanced",
    label: "Balanced",
    icon: "◎",
    color: "#888888",
    description: "Neutral, objective analysis",
    systemNote: "",
  },
  {
    id: "devils_advocate",
    label: "Devil's Advocate",
    icon: "⚡",
    color: "#e05555",
    description: "Aggressively challenge all claims",
    systemNote: `You are playing devil's advocate. Your job is to CHALLENGE every claim, find weaknesses in consensus, and highlight what the models might be getting wrong. Be skeptical of everything, especially where models agree too easily. Look for hidden assumptions, logical gaps, and counterexamples.`,
  },
  {
    id: "scientific",
    label: "Scientific",
    icon: "⊕",
    color: "#4A8FD9",
    description: "Peer-review rigor & citations",
    systemNote: `You are a scientific peer reviewer. Apply strict academic standards: distinguish between correlation and causation, note effect sizes, flag unsupported empirical claims, identify whether claims are based on consensus science or emerging/contested research. Note what peer-reviewed evidence would be needed to confirm each claim.`,
  },
  {
    id: "legal",
    label: "Legal",
    icon: "◈",
    color: "#E8C84A",
    description: "Legal precision & jurisdiction",
    systemNote: `You are a legal analyst. Examine claims for legal precision: note where laws vary by jurisdiction, flag overgeneralizations about legal rules, distinguish between what is legal vs. illegal vs. ambiguous. Highlight where claims could be misleading in a legal context or where case law might contradict the stated position.`,
  },
];

// ─── Streaming API callers (Feature 5) ───────────────────────────────────────

async function streamClaude(prompt, key, model, onChunk, onDone, onError) {
  try {
    const res = await fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": key,
        "anthropic-version": "2023-06-01",
        "anthropic-dangerous-direct-browser-access": "true",
      },
      body: JSON.stringify({
        model,
        max_tokens: 1024,
        stream: true,
        messages: [{ role: "user", content: prompt }],
      }),
    });
    if (!res.ok) {
      const err = await res.json();
      onError(err.error?.message || "Claude API error");
      return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = "", full = "";
    let inputTokens = 0, outputTokens = 0;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const lines = buf.split("\n");
      buf = lines.pop() || "";
      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        const raw = line.slice(6).trim();
        if (raw === "[DONE]") continue;
        try {
          const p = JSON.parse(raw);
          if (p.type === "message_start" && p.message?.usage) {
            inputTokens = p.message.usage.input_tokens || 0;
          }
          if (p.type === "message_delta" && p.usage) {
            outputTokens = p.usage.output_tokens || 0;
          }
          if (p.type === "content_block_delta" && p.delta?.type === "text_delta") {
            full += p.delta.text;
            onChunk(full);
          }
        } catch {}
      }
    }
    onDone(full, { inputTokens, outputTokens });
  } catch (e) { onError(e.message); }
}

async function streamChatGPT(prompt, key, model, onChunk, onDone, onError) {
  try {
    const res = await fetch("https://api.openai.com/v1/chat/completions", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${key}` },
      body: JSON.stringify({
        model, max_tokens: 1024, stream: true,
        stream_options: { include_usage: true },
        messages: [{ role: "user", content: prompt }],
      }),
    });
    if (!res.ok) {
      const err = await res.json();
      onError(err.error?.message || "ChatGPT API error");
      return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = "", full = "";
    let inputTokens = 0, outputTokens = 0;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const lines = buf.split("\n");
      buf = lines.pop() || "";
      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        const raw = line.slice(6).trim();
        if (raw === "[DONE]") continue;
        try {
          const p = JSON.parse(raw);
          if (p.usage) {
            inputTokens = p.usage.prompt_tokens || 0;
            outputTokens = p.usage.completion_tokens || 0;
          }
          const delta = p.choices?.[0]?.delta?.content;
          if (delta) { full += delta; onChunk(full); }
        } catch {}
      }
    }
    onDone(full, { inputTokens, outputTokens });
  } catch (e) { onError(e.message); }
}

async function streamGemini(prompt, key, model, onChunk, onDone, onError) {
  try {
    const res = await fetch(
      `https://generativelanguage.googleapis.com/v1beta/models/${model}:streamGenerateContent?alt=sse&key=${key}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contents: [{ parts: [{ text: prompt }] }] }),
      }
    );
    if (!res.ok) {
      const err = await res.json();
      onError(err.error?.message || "Gemini API error");
      return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = "", full = "";
    let inputTokens = 0, outputTokens = 0;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const lines = buf.split("\n");
      buf = lines.pop() || "";
      for (const line of lines) {
        if (!line.startsWith("data: ")) continue;
        const raw = line.slice(6).trim();
        try {
          const p = JSON.parse(raw);
          // Gemini sends usageMetadata in the final chunk
          if (p.usageMetadata) {
            inputTokens = p.usageMetadata.promptTokenCount || 0;
            outputTokens = p.usageMetadata.candidatesTokenCount || 0;
          }
          const text = p.candidates?.[0]?.content?.parts?.map((pt) => pt.text || "").join("") || "";
          if (text) { full += text; onChunk(full); }
        } catch {}
      }
    }
    onDone(full, { inputTokens, outputTokens });
  } catch (e) { onError(e.message); }
}

const STREAM_CALLERS = {
  claude: streamClaude,
  chatgpt: streamChatGPT,
  gemini: streamGemini,
};

// ─── Non-streaming callers (analysis + web search) ───────────────────────────
async function callClaude(prompt, key, model = "claude-sonnet-4-6") {
  const res = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST",
    headers: {
      "Content-Type": "application/json", "x-api-key": key,
      "anthropic-version": "2023-06-01", "anthropic-dangerous-direct-browser-access": "true",
    },
    body: JSON.stringify({ model, max_tokens: 1024, messages: [{ role: "user", content: prompt }] }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error?.message || "Claude API error");
  return data.content.map((c) => c.text || "").join("\n");
}

async function callClaudeWithWebSearch(prompt, key) {
  const messages = [{ role: "user", content: prompt }];
  let allContent = [];
  while (true) {
    const res = await fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: {
        "Content-Type": "application/json", "x-api-key": key,
        "anthropic-version": "2023-06-01", "anthropic-dangerous-direct-browser-access": "true",
      },
      body: JSON.stringify({
        model: "claude-sonnet-4-6", max_tokens: 2048,
        tools: [{ type: "web_search_20250305", name: "web_search" }],
        messages,
      }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error?.message || "Claude web search error");
    allContent = data.content;
    messages.push({ role: "assistant", content: data.content });
    if (data.stop_reason !== "tool_use") break;
    const toolResults = data.content
      .filter((b) => b.type === "tool_use")
      .map((b) => ({ type: "tool_result", tool_use_id: b.id, content: JSON.stringify(b.input) }));
    messages.push({ role: "user", content: toolResults });
  }
  return allContent.filter((b) => b.type === "text").map((b) => b.text).join("\n");
}

// ─── Prompts ──────────────────────────────────────────────────────────────────
const ANALYSIS_PROMPT = (responses, personaNote = "") => `You are a fact-checking analyst.${personaNote ? `\n\n${personaNote}` : ""} You have responses from multiple AI models to the same question.\n\n${responses.map((r) => `### ${r.label}:\n${r.text}`).join("\n\n")}\n\nProduce a structured fact-check report:\n\n1. **CONSENSUS** - Claims ALL or most models agree on (bullet points). At the end, list the agreeing models like: [AGREE: Claude, ChatGPT, Gemini]\n2. **DISAGREEMENTS** - Where models contradict each other. For each disagreement name the models like: [CONFLICT: Claude vs ChatGPT]\n3. **UNIQUE CLAIMS** - Facts only one model mentioned (name the model, flag if suspicious)\n4. **CONFIDENCE VERDICT** - HIGH / MEDIUM / LOW with a 1-2 sentence explanation\n5. **WHAT TO VERIFY** - Specific things to double-check with a primary source\n\nBe direct and analytical. Flag anything suspicious.`;

const GROUNDING_PROMPT = (query, consensusClaims) => `You are a fact-checking research assistant. Search the web to verify these consensus claims about: "${query}"\n\nConsensus claims to verify:\n${consensusClaims}\n\nSearch for 2-3 authoritative sources (Wikipedia, academic papers, reputable news, official sites) that either confirm or challenge these claims.\n\nFor each source found, respond with:\n**SOURCE**: [title] — [URL]\n**VERDICT**: Confirms / Contradicts / Partially confirms\n**NOTE**: One sentence on what this source says\n\nKeep it concise. Only include sources you actually find via search.`;

// ─── Section definitions ──────────────────────────────────────────────────────
const SECTION_DEFS = [
  { key: "CONSENSUS",          label: "Consensus",          emoji: "✓", color: "#19C37D" },
  { key: "DISAGREEMENTS",      label: "Disagreements",      emoji: "⚡", color: "#D97B4A" },
  { key: "UNIQUE CLAIMS",      label: "Unique Claims",      emoji: "◈", color: "#4A8FD9" },
  { key: "CONFIDENCE VERDICT", label: "Confidence Verdict", emoji: "◎", color: "#A088C0" },
  { key: "WHAT TO VERIFY",     label: "What to Verify",     emoji: "⚑", color: "#E8C84A" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function parseAnalysis(text) {
  const sections = [];
  for (const def of SECTION_DEFS) {
    const k = def.key.replace(/ /g, "[ _]?");
    const regex = new RegExp(
      `(?:#{1,3}\\s*\\d*\\.?\\s*\\*{0,2}${k}\\*{0,2}|` +
      `\\*{1,2}${k}\\*{1,2}|` +
      `\\d+\\.\\s+\\*{0,2}${k}\\*{0,2})` +
      `[:\\s\\-]*([\\s\\S]*?)` +
      `(?=(?:#{1,3}|\\*{1,2}|\\d+\\.)\\s*(?:CONSENSUS|DISAGREEMENTS|UNIQUE|CONFIDENCE|WHAT)|$)`,
      "i"
    );
    const match = text.match(regex);
    if (match) sections.push({ ...def, content: match[1].trim() });
  }
  return sections.length > 0 ? sections : null;
}

function extractConfidenceLevel(text) {
  if (!text) return null;
  const near = text.match(/(?:confidence|verdict)[^.:\n]*?[:\s]+(HIGH|MEDIUM|LOW)/i);
  if (near) return near[1].toUpperCase();
  const match = text.match(/\b(HIGH|MEDIUM|LOW)\b/i);
  return match ? match[1].toUpperCase() : null;
}

function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g);
  return parts.map((part, i) => {
    if (/^\*\*(.+)\*\*$/.test(part)) return React.createElement("strong", { key: i }, part.slice(2, -2));
    if (/^\*(.+)\*$/.test(part))     return React.createElement("em",     { key: i }, part.slice(1, -1));
    if (/^`(.+)`$/.test(part))       return React.createElement("code",   { key: i, style: { fontFamily: "monospace", fontSize: "0.9em", opacity: 0.85 } }, part.slice(1, -1));
    return part;
  });
}

function renderMarkdown(text) {
  if (!text) return null;
  return text.split("\n").map((line, i) => {
    const headingMatch = line.match(/^(#{1,3})\s+(.*)/);
    if (headingMatch) {
      const sizes = { 1: 16, 2: 14, 3: 13 };
      return React.createElement("div", { key: i, style: { fontWeight: 600, fontSize: sizes[headingMatch[1].length] || 13, marginTop: i > 0 ? 10 : 0, marginBottom: 2 } }, renderInline(headingMatch[2]));
    }
    if (/^---+$/.test(line.trim())) return React.createElement("hr", { key: i, style: { border: "none", borderTop: "1px solid currentColor", opacity: 0.15, margin: "8px 0" } });
    if (!line.trim()) return React.createElement("div", { key: i, style: { height: 6 } });
    const bulletMatch = line.match(/^(\s*)[*\-]\s+(.*)/);
    if (bulletMatch) return React.createElement("div", { key: i, style: { display: "flex", gap: 8, paddingLeft: bulletMatch[1].length * 8 } },
      React.createElement("span", { style: { flexShrink: 0, opacity: 0.5 } }, "·"),
      React.createElement("span", null, ...renderInline(bulletMatch[2]))
    );
    const numMatch = line.match(/^(\s*)\d+\.\s+(.*)/);
    if (numMatch) return React.createElement("div", { key: i, style: { paddingLeft: numMatch[1].length * 8 } }, ...renderInline(line));
    return React.createElement("div", { key: i }, ...renderInline(line));
  });
}

function parseModelAlignment(analysisText) {
  const alignment = {};
  if (!analysisText) return alignment;
  [...analysisText.matchAll(/\[AGREE:\s*([^\]]+)\]/gi)].forEach((m) =>
    m[1].split(",").forEach((name) => {
      const model = MODELS.find((mo) => mo.label.toLowerCase() === name.trim().toLowerCase());
      if (model && !alignment[model.id]) alignment[model.id] = "agree";
    })
  );
  [...analysisText.matchAll(/\[CONFLICT:\s*([^\]]+)\]/gi)].forEach((m) =>
    m[1].split(/\s+vs\s+|,/i).forEach((name) => {
      const model = MODELS.find((mo) => mo.label.toLowerCase() === name.trim().toLowerCase());
      if (model) alignment[model.id] = "conflict";
    })
  );
  return alignment;
}

function parseSources(text) {
  if (!text) return [];
  return text.split(/\*\*SOURCE\*\*:/i).slice(1).map((block) => {
    const titleMatch = block.match(/^([^—\n]+)/);
    const verdictMatch = block.match(/\*\*VERDICT\*\*:\s*(.+?)(?:\n|$)/i);
    const noteMatch = block.match(/\*\*NOTE\*\*:\s*(.+?)(?:\n|$)/i);
    const urlMatch = block.match(/https?:\/\/[^\s\])"]+/);
    return { title: titleMatch?.[1].trim(), url: urlMatch?.[0], verdict: verdictMatch?.[1].trim(), note: noteMatch?.[1].trim() };
  }).filter((s) => s.title);
}

function buildExportText({ prompt, sections, responses, sources, modelVersions, timestamp, personaId, annotations }) {
  const line = "─".repeat(60);
  const date = new Date(timestamp).toLocaleString("en-US", { dateStyle: "long", timeStyle: "short" });
  const persona = PERSONA_MODES.find((p) => p.id === personaId);
  let out = [];
  out.push("MULTI-AI FACT-CHECK REPORT v8.0");
  out.push(line);
  out.push(`Date:     ${date}`);
  out.push(`Question: ${prompt}`);
  if (persona?.id !== "balanced") out.push(`Mode:     ${persona?.label || ""}`);
  if (modelVersions) {
    const used = MODELS.filter((m) => modelVersions[m.id]).map((m) => {
      const vDef = m.versions.find((v) => v.id === modelVersions[m.id]);
      return `${m.label} (${vDef?.label || modelVersions[m.id]})`;
    });
    out.push(`Models:   ${used.join(", ")}`);
  }
  out.push("");
  if (sections) {
    for (const sec of sections) {
      out.push(`▸ ${sec.label.toUpperCase()}`);
      let content = sec.content;
      // Add annotation markers to export
      if (annotations?.[sec.key]) {
        const lines = content.split("\n");
        const annotated = lines.map((ln, i) => {
          const ann = annotations[sec.key][i];
          if (ann === "verified") return `${ln}  ✓ [VERIFIED]`;
          if (ann === "disputed") return `${ln}  ✗ [DISPUTED]`;
          return ln;
        });
        content = annotated.join("\n");
      }
      out.push(content);
      out.push("");
    }
  }
  if (responses && Object.keys(responses).length > 0) {
    out.push(line);
    out.push("RAW AI RESPONSES");
    out.push("");
    for (const m of MODELS) {
      if (responses[m.id]) {
        out.push(`[ ${m.label} ]`);
        out.push(responses[m.id]);
        out.push("");
      }
    }
  }
  if (sources && sources.length > 0) {
    out.push(line);
    out.push("LIVE WEB SOURCES");
    out.push("");
    sources.forEach((s, i) => {
      out.push(`${i + 1}. ${s.title}`);
      if (s.url)     out.push(`   ${s.url}`);
      if (s.verdict) out.push(`   Verdict: ${s.verdict}`);
      if (s.note)    out.push(`   ${s.note}`);
      out.push("");
    });
  }
  out.push(line);
  out.push("Generated by Multi-AI Fact-Check Engine v7.1");
  return out.join("\n");
}


// ─── Annotation helpers (Feature 2) ──────────────────────────────────────────
function cycleAnnotation(current) {
  if (!current) return "verified";
  if (current === "verified") return "disputed";
  return null;
}
const ANNOTATABLE_SECTIONS = new Set(["CONSENSUS", "DISAGREEMENTS", "WHAT TO VERIFY"]);

// ─── ConfidenceMeter ──────────────────────────────────────────────────────────
function ConfidenceMeter({ level }) {
  const config = {
    HIGH:   { color: "#19C37D", bars: 3, label: "High Confidence" },
    MEDIUM: { color: "#E8C84A", bars: 2, label: "Medium Confidence" },
    LOW:    { color: "#e05555", bars: 1, label: "Low Confidence" },
  };
  const c = config[level];
  if (!c) return null;
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "5px 12px", background: c.color + "12", border: `1px solid ${c.color}33` }}>
      <div style={{ display: "flex", gap: 3, alignItems: "flex-end" }}>
        {[1, 2, 3].map((i) => <div key={i} style={{ width: 4, height: 6 + i * 4, background: i <= c.bars ? c.color : "#2a2a2a", transition: "background 0.3s" }} />)}
      </div>
      <span style={{ fontSize: 11, fontFamily: "monospace", color: c.color, letterSpacing: "0.1em" }}>{c.label}</span>
    </div>
  );
}

// ─── Persona Selector (Feature 4) ────────────────────────────────────────────
function PersonaSelector({ selected, onSelect, T }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 10, letterSpacing: "0.2em", color: T.textGhost, textTransform: "uppercase", fontFamily: "monospace", marginBottom: 8 }}>
        Analysis Mode
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {PERSONA_MODES.map((p) => {
          const isSelected = selected === p.id;
          const isBalanced = p.id === "balanced";
          const selBg     = isBalanced ? (T.isDark ? "#3a3a3a" : "#d0ccc6") : p.color + "20";
          const selBorder = isBalanced ? (T.isDark ? "#cccccc"  : "#555555") : p.color;
          const selColor  = isBalanced ? (T.isDark ? "#ffffff"  : "#111111") : p.color;
          return (
            <button key={p.id} onClick={() => onSelect(p.id)}
              title={p.description}
              style={{
                padding: "6px 14px", display: "flex", alignItems: "center", gap: 6,
                background:    isSelected ? selBg     : T.bgInput,
                border:        `1px solid ${isSelected ? selBorder : T.borderMid}`,
                color:         isSelected ? selColor  : T.textDim,
                fontSize: 11, fontFamily: "monospace", cursor: "pointer",
                letterSpacing: "0.06em", transition: "all 0.15s",
              }}
              onMouseEnter={(e) => { if (!isSelected) { e.currentTarget.style.borderColor = T.borderHi; e.currentTarget.style.color = T.text; } }}
              onMouseLeave={(e) => { if (!isSelected) { e.currentTarget.style.borderColor = T.borderMid; e.currentTarget.style.color = T.textDim; } }}>
              <span style={{ fontSize: 11 }}>{p.icon}</span>
              {p.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Progress Bar ─────────────────────────────────────────────────────────────
function ProgressBar({ statuses, T }) {
  const total = MODELS.length;
  const done = MODELS.filter((m) => ["done", "error", "skipped"].includes(statuses[m.id])).length;
  const active = MODELS.some((m) => statuses[m.id] === "loading");
  if (!active && done === 0) return null;
  const pct = (done / total) * 100;
  return (
    <div style={{ marginBottom: 20, animation: "fadeIn 0.3s ease" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontSize: 11, fontFamily: "monospace", color: T.textFaint, letterSpacing: "0.1em" }}>Querying models</span>
        <span style={{ fontSize: 11, fontFamily: "monospace", color: T.textFaint }}>{done}/{total}</span>
      </div>
      <div style={{ height: 2, background: T.border, position: "relative", overflow: "hidden" }}>
        <div style={{ height: "100%", background: "#D97B4A", width: `${pct}%`, transition: "width 0.4s ease" }} />
        {active && <div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "linear-gradient(90deg, transparent, rgba(217,123,74,0.3), transparent)", animation: "shimmer 1.5s infinite" }} />}
      </div>
      <div style={{ display: "flex", gap: 12, marginTop: 8, flexWrap: "wrap" }}>
        {MODELS.map((m) => {
          const s = statuses[m.id];
          const dotColor = s === "done" ? m.color : s === "loading" ? m.color + "88" : s === "error" ? "#e05555" : T.border;
          return (
            <div key={m.id} style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: dotColor, transition: "background 0.3s", animation: s === "loading" ? "pulse 1s infinite" : "none" }} />
              <span style={{ fontSize: 10, fontFamily: "monospace", color: dotColor, letterSpacing: "0.05em" }}>{m.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}




// ─── Pricing (per 1M tokens, USD) ─────────────────────────────────────────────
const DEFAULT_PRICING = {
  // Claude — https://www.anthropic.com/pricing
  "claude-opus-4-6":           { in: 15.00, out: 75.00 },
  "claude-sonnet-4-6":         { in: 3.00,  out: 15.00 },
  "claude-haiku-4-5-20251001": { in: 0.80,  out: 4.00  },
  // OpenAI — https://openai.com/api/pricing
  "gpt-4o":                    { in: 2.50,  out: 10.00 },
  "gpt-4o-mini":               { in: 0.15,  out: 0.60  },
  "gpt-4-turbo":               { in: 10.00, out: 30.00 },
  // Gemini — https://ai.google.dev/pricing
  "gemini-2.5-flash":          { in: 0.15,  out: 0.60  },
  "gemini-2.5-pro":            { in: 1.25,  out: 10.00 },
  "gemini-2.5-flash-lite":     { in: 0.10,  out: 0.40  },
};

const PRICING_LS_KEY    = "ai_fc_pricing";
const PRICING_TS_KEY    = "ai_fc_pricing_ts";

function loadPricing() {
  try {
    const stored = localStorage.getItem(PRICING_LS_KEY);
    return stored ? JSON.parse(stored) : DEFAULT_PRICING;
  } catch { return DEFAULT_PRICING; }
}
function savePricing(p, ts) {
  localStorage.setItem(PRICING_LS_KEY, JSON.stringify(p));
  localStorage.setItem(PRICING_TS_KEY, String(ts));
}
function loadPricingTs() {
  try { return parseInt(localStorage.getItem(PRICING_TS_KEY) || "0", 10); } catch { return 0; }
}

// ─── Storage ──────────────────────────────────────────────────────────────────
function loadKeys() {
  try {
    const raw = JSON.parse(localStorage.getItem("ai_fc_keys") || "{}");
    const validIds = new Set(MODELS.map(m => m.id));
    return Object.fromEntries(Object.entries(raw).filter(([k]) => validIds.has(k)));
  } catch { return {}; }
}
function saveKeys(k)         { localStorage.setItem("ai_fc_keys",           JSON.stringify(k)); }
function loadVersions() {
  try {
    const raw = JSON.parse(localStorage.getItem("ai_fc_versions") || "{}");
    const validIds = new Set(MODELS.map(m => m.id));
    return Object.fromEntries(Object.entries(raw).filter(([k]) => validIds.has(k)));
  } catch { return {}; }
}
function saveVersions(v)     { localStorage.setItem("ai_fc_versions",       JSON.stringify(v)); }
function loadHistory()       { try { return JSON.parse(localStorage.getItem("ai_fc_history")        || "[]"); } catch { return []; } }
function saveHistory(h)      { localStorage.setItem("ai_fc_history",        JSON.stringify(h)); }
function loadUsageLog()      { try { return JSON.parse(localStorage.getItem("ai_fc_usage_log")      || "[]"); } catch { return []; } }
function saveUsageLog(u)     { localStorage.setItem("ai_fc_usage_log",      JSON.stringify(u.slice(-500))); }
function loadSessionTotals() { try { return JSON.parse(localStorage.getItem("ai_fc_session_totals") || "{}"); } catch { return {}; } }
function saveSessionTotals(s){ localStorage.setItem("ai_fc_session_totals", JSON.stringify(s)); }
function loadDark()          { try { return JSON.parse(localStorage.getItem("ai_fc_dark")           ?? "true"); } catch { return true; } }
function saveDark(v)         { localStorage.setItem("ai_fc_dark",           JSON.stringify(v)); }
function loadPersona()       { try { return localStorage.getItem("ai_fc_persona") || "balanced"; } catch { return "balanced"; } }
function savePersona(v)      { localStorage.setItem("ai_fc_persona",        v); }

// ─── Tutorial Overlay (first-load onboarding) ────────────────────────────────
const TUTORIAL_STORAGE_KEY = "ai_fc_tutorial_seen";

function TutorialOverlay({ T, onComplete }) {
  const [step, setStep] = React.useState(0);

  const steps = [
    {
      icon: "◈",
      title: "Welcome to Multi AI Fact-Check Engine",
      body: "This tool queries up to 3 AI models in parallel, then synthesizes their answers into a structured fact-check report — so you can see where they agree, disagree, and what to double-check.",
      accent: "#D97B4A",
    },
    {
      icon: "⚙",
      title: "Step 1 — Add Your API Keys",
      body: "Click the ⚙ APIs button (top right) to add at least 2 API keys. Claude is required for the synthesis step. Gemini and Claude both offer free tiers — a great place to start.",
      accent: "#19C37D",
      items: [
        { label: "Claude", note: "Required for synthesis · free tier", color: "#D97B4A" },
        { label: "Gemini", note: "Free tier available", color: "#4A8FD9" },
        { label: "ChatGPT", note: "Paid plan required", color: "#19C37D" },
      ],
    },
    {
      icon: "◎",
      title: "Step 2 — Choose an Analysis Mode",
      body: "Pick how you want the report framed. Balanced is the default — but you can go Devil's Advocate to stress-test claims, Scientific for peer-review rigor, or Legal for jurisdiction-aware analysis.",
      accent: "#4A8FD9",
      items: [
        { label: "◎ Balanced", note: "Neutral, objective", color: "#888" },
        { label: "⚡ Devil's Advocate", note: "Challenge everything", color: "#e05555" },
        { label: "⊕ Scientific", note: "Peer-review rigor", color: "#4A8FD9" },
        { label: "◈ Legal", note: "Jurisdiction-aware", color: "#E8C84A" },
      ],
    },
    {
      icon: "⊞",
      title: "Step 3 — Ask & Explore",
      body: "Type any question or claim, hit ⌘+Enter (or the Run button), and watch all AIs respond live. The engine synthesizes consensus, disagreements, unique claims, and a confidence verdict — then verifies key claims with web search.",
      accent: "#E8C84A",
      items: [
        { label: "Live streaming", note: "See each AI respond in real-time", color: "#D97B4A" },
        { label: "Annotate claims", note: "Click lines to mark verified/disputed", color: "#19C37D" },
        { label: "Export reports", note: "Copy or save the full analysis", color: "#4A8FD9" },
        { label: "Track costs", note: "Monitor token usage & spending", color: "#E8C84A" },
      ],
    },
    {
      icon: "✓",
      title: "You're All Set",
      body: "Your data stays 100% local — nothing is sent to our servers. API keys go directly to each provider. You can re-access this tutorial from the Changelog (bottom right).",
      accent: "#19C37D",
    },
  ];

  const current = steps[step];
  const isLast = step === steps.length - 1;
  const isFirst = step === 0;

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 10000, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)", animation: "fadeIn 0.3s ease" }}>
      <div style={{ width: "100%", maxWidth: 520, margin: "0 20px", background: T.bgModal, border: `1px solid ${T.borderHi}`, boxShadow: "0 24px 80px rgba(0,0,0,0.5)", animation: "fadeIn 0.3s ease" }}>
        {/* Progress dots */}
        <div style={{ display: "flex", justifyContent: "center", gap: 8, padding: "20px 0 0" }}>
          {steps.map((_, i) => (
            <div key={i} style={{ width: i === step ? 20 : 6, height: 6, background: i === step ? current.accent : T.border, transition: "all 0.3s", cursor: "pointer" }}
              onClick={() => setStep(i)} />
          ))}
        </div>

        {/* Content */}
        <div style={{ padding: "28px 36px 24px", textAlign: "center" }} key={step}>
          <div style={{ fontSize: 36, marginBottom: 16, color: current.accent, opacity: 0.85 }}>{current.icon}</div>
          <h2 style={{ fontSize: 18, fontWeight: 500, margin: "0 0 12px", color: T.text, fontFamily: "Georgia, serif", letterSpacing: "-0.01em" }}>{current.title}</h2>
          <p style={{ fontSize: 12, fontFamily: "monospace", color: T.textDim, lineHeight: 1.7, margin: "0 0 20px" }}>{current.body}</p>

          {current.items && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 20, textAlign: "left" }}>
              {current.items.map((item, i) => (
                <div key={i} style={{ padding: "8px 12px", background: item.color + "08", border: `1px solid ${item.color}22`, display: "flex", flexDirection: "column", gap: 2 }}>
                  <span style={{ fontSize: 11, fontFamily: "monospace", color: item.color, fontWeight: 600 }}>{item.label}</span>
                  <span style={{ fontSize: 10, fontFamily: "monospace", color: T.textFaint }}>{item.note}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Navigation */}
        <div style={{ padding: "0 36px 28px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {!isFirst ? (
            <button onClick={() => setStep(step - 1)}
              style={{ padding: "8px 18px", background: "transparent", border: `1px solid ${T.borderHi}`, color: T.textDim, fontSize: 11, fontFamily: "monospace", cursor: "pointer", letterSpacing: "0.08em" }}>
              ← Back
            </button>
          ) : (
            <button onClick={onComplete}
              style={{ padding: "8px 18px", background: "transparent", border: `1px solid ${T.borderMid}`, color: T.textGhost, fontSize: 11, fontFamily: "monospace", cursor: "pointer", letterSpacing: "0.08em" }}>
              Skip
            </button>
          )}
          <button onClick={isLast ? onComplete : () => setStep(step + 1)}
            style={{ padding: "8px 22px", background: current.accent, border: "none", color: "#0a0a0a", fontSize: 11, fontFamily: "monospace", cursor: "pointer", letterSpacing: "0.12em", fontWeight: 600 }}>
            {isLast ? "Get Started →" : "Next →"}
          </button>
        </div>
      </div>
    </div>
  );
}

function downloadJSON(data, filename) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

function formatTime(ts) {
  const d = new Date(ts);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + " · " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function calcCost(modelVersionId, inputTokens, outputTokens, pricing) {
  const table = pricing || DEFAULT_PRICING;
  const p = table[modelVersionId];
  if (!p) return null;
  return (inputTokens / 1_000_000) * p.in + (outputTokens / 1_000_000) * p.out;
}

function formatCost(usd) {
  if (usd === null || usd === undefined) return "—";
  if (usd < 0.0001) return "<$0.0001";
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(4)}`;
}

function formatTokens(n) {
  if (!n) return "0";
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

// ─── Cost Trend Chart ────────────────────────────────────────────────────────
function CostTrendChart({ buckets, T }) {
  if (!buckets || buckets.length < 2) return (
    <div style={{ height: 90, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <span style={{ fontSize: 10, fontFamily: "monospace", color: T.textDeep }}>Need 2+ data points to render trend</span>
    </div>
  );

  const W = 500, H = 90, padL = 42, padR = 10, padT = 8, padB = 20;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;
  const n = buckets.length;

  const maxCost = Math.max(...buckets.map(b => b.totalCost), 0.000001);
  const scaleX = i => padL + (n === 1 ? plotW / 2 : (i / (n - 1)) * plotW);
  const scaleY = v => padT + plotH - Math.max(0, Math.min(1, v / maxCost)) * plotH;

  const activeModels = MODELS.filter(m => buckets.some(b => (b.perModel[m.id] || 0) > 0));
  const modelPaths = activeModels.map(m => ({
    model: m,
    points: buckets.map((b, i) => `${scaleX(i).toFixed(1)},${scaleY(b.perModel[m.id] || 0).toFixed(1)}`).join(" "),
  }));

  const gridVals = [maxCost, maxCost * 0.5];
  const labelStep = Math.max(1, Math.ceil(n / 6));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: H, display: "block" }}>
      {/* Grid lines */}
      {gridVals.map((v, gi) => (
        <React.Fragment key={gi}>
          <line x1={padL} y1={scaleY(v).toFixed(1)} x2={W - padR} y2={scaleY(v).toFixed(1)}
            stroke={T.border} strokeWidth="0.5" strokeDasharray="2,3" />
          <text x={padL - 3} y={scaleY(v) + 3} textAnchor="end" fontSize="7" fill={T.textDeep} fontFamily="monospace">
            {formatCost(v)}
          </text>
        </React.Fragment>
      ))}

      {/* Baseline */}
      <line x1={padL} y1={(padT + plotH).toFixed(1)} x2={W - padR} y2={(padT + plotH).toFixed(1)}
        stroke={T.borderMid} strokeWidth="0.5" />

      {/* Per-model area fills */}
      {modelPaths.map(({ model, points }) => {
        const areaPoints =
          `${scaleX(0).toFixed(1)},${(padT + plotH).toFixed(1)} ` +
          points +
          ` ${scaleX(n - 1).toFixed(1)},${(padT + plotH).toFixed(1)}`;
        return (
          <polygon key={model.id + "_area"} points={areaPoints}
            fill={model.color} opacity={0.06} />
        );
      })}

      {/* Per-model lines */}
      {modelPaths.map(({ model, points }) => (
        <polyline key={model.id} points={points} fill="none"
          stroke={model.color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" opacity={0.85} />
      ))}

      {/* End dots per model */}
      {modelPaths.map(({ model }) => {
        const lastIdx = [...buckets].map((b, i) => ({ v: b.perModel[model.id] || 0, i }))
          .filter(x => x.v > 0).pop();
        if (!lastIdx) return null;
        return (
          <circle key={model.id + "_dot"}
            cx={scaleX(lastIdx.i).toFixed(1)} cy={scaleY(lastIdx.v).toFixed(1)}
            r="2.5" fill={model.color} />
        );
      })}

      {/* X axis labels */}
      {buckets.map((b, i) => {
        if (i % labelStep !== 0 && i !== n - 1) return null;
        return (
          <text key={i} x={scaleX(i).toFixed(1)} y={H - 3}
            textAnchor={i === n - 1 && n > 1 ? "end" : i === 0 ? "start" : "middle"}
            fontSize="7" fill={T.textDeep} fontFamily="monospace">
            {b.label}
          </text>
        );
      })}
    </svg>
  );
}

// ─── Usage Tracker Panel ───────────────────────────────────────────────────────
const TIME_RANGES = [
  { id: "session", label: "Session" },
  { id: "day",     label: "Today"   },
  { id: "week",    label: "Week"    },
  { id: "month",   label: "Month"   },
  { id: "all",     label: "All Time"},
  { id: "custom",  label: "Custom"  },
];

function UsageTrackerPanel({ usageLog, pricing, pricingTs, pricingLoading, onRefreshPricing, onResetPricing, hasClaudeKey, T, onClear, onExportJSON, sessionStartTs }) {
  const [open, setOpen]             = useState(false);
  const [showLog, setShowLog]       = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [timeRange, setTimeRange]   = useState("session");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd]   = useState("");

  // ── Filter log by selected time range ──
  const filteredLog = React.useMemo(() => {
    const startOfDay   = new Date(); startOfDay.setHours(0, 0, 0, 0);
    const startOfWeek  = new Date(); startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay()); startOfWeek.setHours(0, 0, 0, 0);
    const startOfMonth = new Date(); startOfMonth.setDate(1); startOfMonth.setHours(0, 0, 0, 0);
    switch (timeRange) {
      case "session": return usageLog.filter(e => e.ts >= sessionStartTs);
      case "day":     return usageLog.filter(e => e.ts >= startOfDay.getTime());
      case "week":    return usageLog.filter(e => e.ts >= startOfWeek.getTime());
      case "month":   return usageLog.filter(e => e.ts >= startOfMonth.getTime());
      case "all":     return usageLog;
      case "custom": {
        const s = customStart ? new Date(customStart).getTime() : 0;
        const eTs = customEnd   ? new Date(customEnd + "T23:59:59").getTime() : Infinity;
        return usageLog.filter(e => e.ts >= s && e.ts <= eTs);
      }
      default: return usageLog;
    }
  }, [usageLog, timeRange, sessionStartTs, customStart, customEnd]);

  // ── Aggregated totals ──
  const totalCost = filteredLog.reduce((s, e) => s + (e.cost || 0), 0);
  const totalIn   = filteredLog.reduce((s, e) => s + (e.inputTokens || 0), 0);
  const totalOut  = filteredLog.reduce((s, e) => s + (e.outputTokens || 0), 0);
  const callCount = filteredLog.length;

  // ── Per-model breakdown ──
  const perModel = React.useMemo(() => {
    const map = {};
    for (const e of filteredLog) {
      if (!map[e.modelId]) map[e.modelId] = { calls: 0, inputTokens: 0, outputTokens: 0, cost: 0 };
      map[e.modelId].calls++;
      map[e.modelId].inputTokens  += e.inputTokens  || 0;
      map[e.modelId].outputTokens += e.outputTokens || 0;
      map[e.modelId].cost         += e.cost         || 0;
    }
    return map;
  }, [filteredLog]);

  // ── Chart time buckets ──
  const chartBuckets = React.useMemo(() => {
    if (filteredLog.length === 0) return [];
    let bucketMs, labelFn;
    if (timeRange === "session" || timeRange === "day") {
      bucketMs = 3_600_000;
      labelFn  = ts => new Date(ts).toLocaleTimeString("en-US", { hour: "numeric", hour12: true });
    } else if (timeRange === "week") {
      bucketMs = 86_400_000;
      labelFn  = ts => new Date(ts).toLocaleDateString("en-US", { weekday: "short" });
    } else {
      bucketMs = 86_400_000;
      labelFn  = ts => new Date(ts).toLocaleDateString("en-US", { month: "numeric", day: "numeric" });
    }
    const groups = {};
    for (const entry of filteredLog) {
      const bTs = Math.floor(entry.ts / bucketMs) * bucketMs;
      if (!groups[bTs]) groups[bTs] = { ts: bTs, label: labelFn(bTs), totalCost: 0, perModel: {} };
      groups[bTs].totalCost += entry.cost || 0;
      groups[bTs].perModel[entry.modelId] = (groups[bTs].perModel[entry.modelId] || 0) + (entry.cost || 0);
    }
    return Object.values(groups).sort((a, b) => a.ts - b.ts);
  }, [filteredLog, timeRange]);

  // ── Pricing helpers ──
  const pricingAge = pricingTs ? (() => {
    const diff = Date.now() - pricingTs;
    const days = Math.floor(diff / 86400000);
    if (days === 0) return "today";
    if (days === 1) return "yesterday";
    return `${days}d ago`;
  })() : null;

  const pricingDate = pricingTs
    ? new Date(pricingTs).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
    : "never refreshed";

  const handleExportTxt = () => {
    const lines = [
      "USAGE LOG — AI Fact-Check Engine",
      "─".repeat(50),
      `Exported:      ${new Date().toLocaleString()}`,
      `Time Range:    ${timeRange}`,
      `Pricing as of: ${pricingDate}`,
      `Total cost:    ${formatCost(totalCost)}`,
      `Total tokens:  ${formatTokens(totalIn)} in / ${formatTokens(totalOut)} out`,
      "",
      "Per-call log:",
      ...filteredLog.map((e, i) =>
        `${i + 1}. [${e.modelLabel}] ${e.version} | in:${e.inputTokens} out:${e.outputTokens} | ${formatCost(e.cost)} | ${new Date(e.ts).toLocaleString()}`
      ),
      "",
      "Per-model totals:",
      ...MODELS.map((m) => {
        const t = perModel[m.id];
        if (!t || !t.calls) return null;
        return `${m.label}: ${t.calls} call(s) | in:${formatTokens(t.inputTokens)} out:${formatTokens(t.outputTokens)} | ${formatCost(t.cost)}`;
      }).filter(Boolean),
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "usage-log.txt"; a.click();
    URL.revokeObjectURL(url);
  };

  const allTimeCost  = usageLog.reduce((s, e) => s + (e.cost || 0), 0);
  const allTimeCount = usageLog.length;

  return (
    <div style={{ marginBottom: 16, border: `1px solid ${T.borderMid}`, borderTop: "2px solid #19C37D55", background: T.bgElevated }}>

      {/* ── Header ── */}
      <div style={{ padding: "10px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: open ? `1px solid ${T.border}` : "none", cursor: "pointer" }} onClick={() => setOpen(!open)}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 11, fontFamily: "monospace", color: "#19C37D", letterSpacing: "0.15em", textTransform: "uppercase" }}>◎ Usage & Cost</span>
          {allTimeCount > 0 ? (
            <>
              <span style={{ fontSize: 10, fontFamily: "monospace", color: T.textGhost }}>{allTimeCount} call{allTimeCount !== 1 ? "s" : ""} all-time</span>
              <span style={{ fontSize: 12, fontFamily: "monospace", color: "#19C37D", fontWeight: 600 }}>{formatCost(allTimeCost)}</span>
            </>
          ) : (
            <span style={{ fontSize: 10, fontFamily: "monospace", color: T.textGhost }}>no calls yet</span>
          )}
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {allTimeCount > 0 && open && (
            <>
              <button onClick={(e) => { e.stopPropagation(); handleExportTxt(); }}
                style={{ background: "none", border: `1px solid ${T.borderHi}`, color: T.textFaint, fontSize: 10, fontFamily: "monospace", cursor: "pointer", padding: "3px 8px", letterSpacing: "0.06em" }}
                title="Export filtered range as .txt">↓ txt</button>
              <button onClick={(e) => { e.stopPropagation(); onExportJSON(); }}
                style={{ background: "none", border: `1px solid ${T.borderHi}`, color: T.textFaint, fontSize: 10, fontFamily: "monospace", cursor: "pointer", padding: "3px 8px", letterSpacing: "0.06em" }}
                title="Export full history as .json">↓ json</button>
              <button onClick={(e) => { e.stopPropagation(); if (mobileConfirm("Clear all usage data?")) onClear(); }}
                style={{ background: "none", border: `1px solid ${T.borderHi}`, color: T.textFaint, fontSize: 10, fontFamily: "monospace", cursor: "pointer", padding: "3px 8px" }}
                title="Clear all usage history">✕</button>
            </>
          )}
          <span style={{ fontSize: 11, color: T.textGhost }}>{open ? "▲" : "▼"}</span>
        </div>
      </div>

      {open && (
        <div style={{ padding: "14px 16px" }}>

          {/* ── Time range selector ── */}
          <div style={{ display: "flex", gap: 4, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
            <span style={{ fontSize: 9, fontFamily: "monospace", color: T.textGhost, letterSpacing: "0.1em", textTransform: "uppercase", marginRight: 4 }}>Range</span>
            {TIME_RANGES.map(r => (
              <button key={r.id} onClick={() => setTimeRange(r.id)}
                style={{
                  padding: "4px 10px", fontSize: 10, fontFamily: "monospace",
                  background: timeRange === r.id ? "#19C37D18" : "none",
                  border: `1px solid ${timeRange === r.id ? "#19C37D88" : T.borderMid}`,
                  color: timeRange === r.id ? "#19C37D" : T.textGhost,
                  cursor: "pointer", letterSpacing: "0.05em", transition: "all 0.15s",
                }}>
                {r.label}
              </button>
            ))}
          </div>

          {/* ── Custom date range ── */}
          {timeRange === "custom" && (
            <div style={{ display: "flex", gap: 8, marginBottom: 14, alignItems: "center", padding: "8px 10px", background: T.bg, border: `1px solid ${T.border}` }}>
              <span style={{ fontSize: 9, fontFamily: "monospace", color: T.textGhost, letterSpacing: "0.08em" }}>From</span>
              <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
                style={{ background: T.bgInput, border: `1px solid ${T.borderMid}`, color: T.text, fontSize: 10, fontFamily: "monospace", padding: "4px 8px" }} />
              <span style={{ fontSize: 9, fontFamily: "monospace", color: T.textGhost, letterSpacing: "0.08em" }}>To</span>
              <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                style={{ background: T.bgInput, border: `1px solid ${T.borderMid}`, color: T.text, fontSize: 10, fontFamily: "monospace", padding: "4px 8px" }} />
              {(customStart || customEnd) && (
                <button onClick={() => { setCustomStart(""); setCustomEnd(""); }}
                  style={{ background: "none", border: "none", color: T.textGhost, fontSize: 10, fontFamily: "monospace", cursor: "pointer", padding: "0 4px" }}>✕ Clear</button>
              )}
            </div>
          )}

          {/* ── Summary stat cards ── */}
          {callCount > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 0, marginBottom: 14, border: `1px solid ${T.border}` }}>
              {[
                { label: "Total Cost",     value: formatCost(totalCost),       color: "#19C37D" },
                { label: "Input Tokens",   value: formatTokens(totalIn),       color: T.textDim },
                { label: "Output Tokens",  value: formatTokens(totalOut),      color: T.textDim },
                { label: "API Calls",      value: String(callCount),           color: T.textDim },
              ].map((s, i) => (
                <div key={i} style={{ padding: "10px 12px", borderRight: i < 3 ? `1px solid ${T.border}` : "none", background: T.bg }}>
                  <div style={{ fontSize: 9, fontFamily: "monospace", color: T.textGhost, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 5 }}>{s.label}</div>
                  <div style={{ fontSize: 14, fontFamily: "monospace", color: s.color, fontWeight: 600 }}>{s.value}</div>
                </div>
              ))}
            </div>
          )}

          {/* ── Trend chart ── */}
          {chartBuckets.length >= 2 && (
            <div style={{ marginBottom: 14, padding: "10px 12px 8px", background: T.bg, border: `1px solid ${T.border}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <span style={{ fontSize: 9, fontFamily: "monospace", color: T.textGhost, letterSpacing: "0.12em", textTransform: "uppercase" }}>Cost Trend</span>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {MODELS.filter(m => chartBuckets.some(b => (b.perModel[m.id] || 0) > 0)).map(m => (
                    <span key={m.id} style={{ fontSize: 8, fontFamily: "monospace", color: m.color, display: "flex", alignItems: "center", gap: 3 }}>
                      <span style={{ display: "inline-block", width: 16, height: 2, background: m.color, opacity: 0.8 }} />
                      {m.label}
                    </span>
                  ))}
                </div>
              </div>
              <CostTrendChart buckets={chartBuckets} T={T} />
            </div>
          )}

          {chartBuckets.length === 1 && (
            <div style={{ marginBottom: 14, padding: "8px 12px", background: T.bg, border: `1px solid ${T.border}` }}>
              <span style={{ fontSize: 10, fontFamily: "monospace", color: T.textGhost }}>
                ◎ 1 data point — run more queries to see the trend chart
              </span>
            </div>
          )}

          {/* ── Per-model breakdown cards ── */}
          {callCount > 0 && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(148px, 1fr))", gap: 8, marginBottom: 14 }}>
              {MODELS.map(m => {
                const t = perModel[m.id];
                if (!t) return null;
                const pct = totalCost > 0 ? (t.cost / totalCost) * 100 : 0;
                return (
                  <div key={m.id} style={{ padding: "10px 12px", background: T.bg, border: `1px solid ${T.border}`, borderLeft: `3px solid ${m.color}`, position: "relative", overflow: "hidden" }}>
                    {/* Cost share bar at bottom */}
                    <div style={{ position: "absolute", bottom: 0, left: 0, height: 2, width: `${pct}%`, background: m.color, opacity: 0.55 }} />
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                      <span style={{ color: m.color, fontSize: 10 }}>{m.icon}</span>
                      <span style={{ fontSize: 10, fontFamily: "monospace", color: m.color, letterSpacing: "0.08em" }}>{m.label}</span>
                      <span style={{ fontSize: 9, fontFamily: "monospace", color: T.textGhost, background: T.border, padding: "0px 4px" }}>{t.calls}×</span>
                    </div>
                    <div style={{ fontSize: 14, fontFamily: "monospace", color: T.text, fontWeight: 600, marginBottom: 4 }}>{formatCost(t.cost)}</div>
                    <div style={{ fontSize: 9, fontFamily: "monospace", color: T.textGhost, lineHeight: 1.65 }}>
                      <div>↑ {formatTokens(t.inputTokens)} in</div>
                      <div>↓ {formatTokens(t.outputTokens)} out</div>
                      <div style={{ color: T.textFaint, marginTop: 2 }}>{pct.toFixed(0)}% of total</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Pricing controls ── */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, padding: "8px 12px", background: T.bg, border: `1px solid ${T.border}` }}>
            <span style={{ fontSize: 10, fontFamily: "monospace", color: T.textFaint, textTransform: "uppercase", letterSpacing: "0.1em" }}>Pricing</span>
            {pricingTs ? (
              <span style={{ fontSize: 10, fontFamily: "monospace", color: T.textGhost }} title={pricingDate}>
                last updated {pricingAge} — {pricingDate}
              </span>
            ) : (
              <span style={{ fontSize: 10, fontFamily: "monospace", color: T.textGhost }}>using built-in defaults</span>
            )}
            <div style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center" }}>
              <button onClick={onRefreshPricing} disabled={pricingLoading}
                style={{ background: "none", border: `1px solid ${hasClaudeKey ? "#19C37D55" : T.borderHi}`, color: pricingLoading ? T.textGhost : hasClaudeKey ? "#19C37D" : T.textFaint, fontSize: 10, fontFamily: "monospace", cursor: pricingLoading ? "not-allowed" : "pointer", padding: "3px 10px", letterSpacing: "0.06em", display: "flex", alignItems: "center", gap: 4 }}
                title={hasClaudeKey ? "Fetch live pricing from official sources via Claude web search" : "Add a Claude API key to enable live pricing fetch"}>
                {pricingLoading ? "⟳ fetching..." : "⟳ Refresh Pricing"}
              </button>
              <button onClick={onResetPricing} disabled={pricingLoading}
                style={{ background: "none", border: `1px solid ${T.borderHi}`, color: T.textGhost, fontSize: 10, fontFamily: "monospace", cursor: pricingLoading ? "not-allowed" : "pointer", padding: "3px 10px", letterSpacing: "0.06em" }}
                title="Reset to built-in default prices">
                ↺ Reset to Default
              </button>
            </div>
            <button onClick={() => setShowPricing(!showPricing)}
              style={{ background: "none", border: `1px solid ${T.borderHi}`, color: T.textGhost, fontSize: 10, fontFamily: "monospace", cursor: "pointer", padding: "3px 8px" }}
              title="View pricing table">
              {showPricing ? "▲" : "▼"} rates
            </button>
          </div>

          {/* ── Pricing table ── */}
          {showPricing && (
            <div style={{ marginBottom: 12, padding: "10px 12px", background: T.bg, border: `1px solid ${T.border}`, fontSize: 10, fontFamily: "monospace", color: T.textGhost }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: "4px 16px", alignItems: "center" }}>
                <span style={{ color: T.textFaint, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>Model</span>
                <span style={{ color: T.textFaint, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>Input/1M</span>
                <span style={{ color: T.textFaint, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>Output/1M</span>
                {MODELS.map((m) => m.versions.map((v) => {
                  const p = (pricing || DEFAULT_PRICING)[v.id];
                  return p ? (
                    <React.Fragment key={v.id}>
                      <span style={{ color: m.color }}>{m.icon} {v.label}</span>
                      <span style={{ color: T.textDim }}>${p.in.toFixed(2)}</span>
                      <span style={{ color: T.textDim }}>${p.out.toFixed(2)}</span>
                    </React.Fragment>
                  ) : null;
                }))}
              </div>
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${T.border}`, color: T.textDeep, lineHeight: 1.6 }}>
                Sources: anthropic.com/pricing · openai.com/api/pricing · ai.google.dev/pricing
              </div>
            </div>
          )}

          {/* ── Per-call log ── */}
          {callCount > 0 && (
            <>
              <button onClick={() => setShowLog(!showLog)}
                style={{ background: "none", border: "none", color: T.textGhost, fontSize: 10, fontFamily: "monospace", cursor: "pointer", padding: 0, letterSpacing: "0.08em", marginBottom: showLog ? 8 : 0 }}>
                {showLog ? "▲ Hide" : "▼ Show"} per-call log ({filteredLog.length})
              </button>
              {showLog && (
                <div style={{ maxHeight: 200, overflowY: "auto", border: `1px solid ${T.border}`, background: T.bg }}>
                  {[...filteredLog].reverse().map((e, i) => {
                    const model = MODELS.find(m => m.id === e.modelId);
                    return (
                      <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 10px", borderBottom: `1px solid ${T.border}`, fontSize: 10, fontFamily: "monospace" }}>
                        <span style={{ color: model?.color || T.textDim, flexShrink: 0, minWidth: 60 }}>{model?.icon} {e.modelLabel}</span>
                        <span style={{ color: T.textGhost, flexShrink: 0, fontSize: 9, minWidth: 80, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.version}</span>
                        <span style={{ color: T.textFaint, flexShrink: 0 }}>↑{formatTokens(e.inputTokens)} ↓{formatTokens(e.outputTokens)}</span>
                        <span style={{ color: "#19C37D", flexShrink: 0, fontWeight: 600 }}>{formatCost(e.cost)}</span>
                        <span style={{ color: T.textDeep, marginLeft: "auto", flexShrink: 0 }}>
                          {new Date(e.ts).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}

          {callCount === 0 && (
            <p style={{ margin: 0, fontSize: 11, fontFamily: "monospace", color: T.textGhost, lineHeight: 1.6 }}>
              {allTimeCount === 0
                ? "No API calls yet. Run a fact-check to start tracking usage & cost."
                : `No calls in this ${timeRange === "all" ? "history" : timeRange === "custom" ? "date range" : timeRange}.`}
            </p>
          )}
        </div>
      )}
    </div>
  );
}


// ─── Model Response Card (Feature 1: Retry button) ────────────────────────────
function ModelResponseCard({ model, status, response, error, alignment, selectedVersion, onRetry, usage, T }) {
  const [expanded, setExpanded] = useState(false);
  const statusStyles = {
    idle:    { color: T.border,       label: "—" },
    loading: { color: model.color,    label: "streaming..." },
    done:    { color: model.color,    label: "done" },
    error:   { color: "#e05555",      label: "error" },
    skipped: { color: T.textGhost,   label: "no key" },
  };
  const st = statusStyles[status] || statusStyles.idle;
  const isLong = response && response.length > 400;
  const displayText = expanded ? response : response?.slice(0, 400);
  const align = alignment?.[model.id];
  const alignConfig = {
    agree:    { label: "Agrees",    color: "#19C37D", bg: "#0a1a0f", symbol: "✓" },
    conflict: { label: "Conflicts", color: "#e05555", bg: "#1a0a0a", symbol: "⚡" },
  };
  const ac = align ? alignConfig[align] : null;
  const vDef = model.versions.find((v) => v.id === selectedVersion);

  return (
    <div style={{ background: T.bgElevated, border: `1px solid ${status === "done" ? model.color + "33" : T.border}`, borderLeft: `3px solid ${status === "done" ? model.color : status === "loading" ? model.color + "88" : T.borderMid}`, transition: "border-color 0.3s" }}>
      <div style={{ padding: "10px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${T.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ color: model.color, fontSize: 11 }}>{model.icon}</span>
          <span style={{ fontSize: 12, fontFamily: "monospace", color: model.color, letterSpacing: "0.1em" }}>{model.label}</span>
          {vDef && <span style={{ fontSize: 9, fontFamily: "monospace", color: T.textFaint, background: T.border, padding: "1px 6px", letterSpacing: "0.05em" }}>{vDef.label}</span>}
          {ac && <span style={{ fontSize: 9, fontFamily: "monospace", padding: "1px 6px", background: ac.bg, color: ac.color, border: `1px solid ${ac.color}33`, letterSpacing: "0.1em" }}>{ac.symbol} {ac.label}</span>}
          {status === "done" && usage && (
            <span style={{ fontSize: 9, fontFamily: "monospace", color: T.textGhost, background: T.bg, padding: "1px 6px", border: `1px solid ${T.border}` }}>
              {formatTokens(usage.inputTokens)}↑ {formatTokens(usage.outputTokens)}↓ {formatCost(usage.cost)}
            </span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {/* Feature 1: Retry button */}
          {(status === "done" || status === "error") && onRetry && (
            <button onClick={() => onRetry(model.id)} title="Re-query this model"
              style={{ background: "none", border: `1px solid ${T.borderHi}`, color: T.textDim, fontSize: 11, fontFamily: "monospace", cursor: "pointer", padding: "2px 8px", letterSpacing: "0.06em", transition: "all 0.15s" }}
              onMouseEnter={(e) => { e.currentTarget.style.color = model.color; e.currentTarget.style.borderColor = model.color + "55"; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = T.textDim; e.currentTarget.style.borderColor = T.borderHi; }}>
              ↺ retry
            </button>
          )}
          <span style={{ fontSize: 11, fontFamily: "monospace", color: st.color, letterSpacing: "0.1em" }}>
            {status === "loading" ? <span style={{ animation: "pulse 1s infinite" }}>◈ {st.label}</span> : st.label}
          </span>
        </div>
      </div>
      <div style={{ padding: "14px 16px", minHeight: 60 }}>
        {status === "idle"    && <span style={{ fontSize: 12, color: T.textDeep, fontFamily: "monospace" }}>Waiting...</span>}
        {status === "loading" && response && (
          <div style={{ fontSize: 13, lineHeight: 1.7, color: T.textMuted, fontFamily: "Georgia, serif" }}>
            {renderMarkdown(response)}
            <span style={{ display: "inline-block", width: 8, height: 14, background: model.color, opacity: 0.7, animation: "pulse 0.8s infinite", verticalAlign: "text-bottom", marginLeft: 2 }} />
          </div>
        )}
        {status === "loading" && !response && (
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            {[0, 1, 2].map((i) => (
              <span key={i} style={{ display: "inline-block", width: 5, height: 5, borderRadius: "50%", background: model.color, opacity: 0.4, animation: `bounce 1s ease-in-out ${i * 0.2}s infinite` }} />
            ))}
          </div>
        )}
        {status === "done" && response && (
          <>
            <div style={{ fontSize: 13, lineHeight: 1.7, color: T.textMuted, fontFamily: "Georgia, serif" }}>
              {renderMarkdown(displayText)}
              {!expanded && isLong ? <span style={{ opacity: 0.5 }}>…</span> : null}
            </div>
            {isLong && <button onClick={() => setExpanded(!expanded)} style={{ marginTop: 8, background: "none", border: "none", color: model.color + "99", fontSize: 11, fontFamily: "monospace", cursor: "pointer", padding: 0, letterSpacing: "0.08em" }}>{expanded ? "▲ Show less" : "▼ Show full response"}</button>}
          </>
        )}
        {status === "error"   && <span style={{ fontSize: 12, color: "#e05555", fontFamily: "monospace" }}>⚠ {error}</span>}
        {status === "skipped" && <div style={{ display: "flex", alignItems: "center", gap: 10 }}><span style={{ fontSize: 12, color: T.textGhost, fontFamily: "monospace" }}>No API key —</span><a href={model.docsUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: model.color + "99", fontFamily: "monospace", textDecoration: "none" }}>Get key →</a></div>}
      </div>
    </div>
  );
}

// ─── Annotated Section (Feature 2) ───────────────────────────────────────────
function AnnotatedSection({ section, annotations, onAnnotate, T }) {
  const canAnnotate = ANNOTATABLE_SECTIONS.has(section.key);
  const lines = section.content.split("\n");

  const annColors = { verified: "#19C37D", disputed: "#e05555" };
  const annIcons  = { verified: "✓", disputed: "✗" };
  const annLabels = { verified: "verified", disputed: "disputed" };

  return (
    <div style={{ padding: "14px 18px", fontSize: 14, lineHeight: 1.8, color: T.textMuted, fontFamily: "Georgia, serif" }}>
      {lines.map((line, i) => {
        // Headings — never annotatable
        const headingMatch = line.match(/^(#{1,3})\s+(.*)/);
        if (headingMatch) {
          const sizes = { 1: 16, 2: 14, 3: 13 };
          return <div key={i} style={{ fontWeight: 600, fontSize: sizes[headingMatch[1].length] || 13, marginTop: i > 0 ? 10 : 0, marginBottom: 2 }}>{renderInline(headingMatch[2])}</div>;
        }
        if (/^---+$/.test(line.trim())) return <hr key={i} style={{ border: "none", borderTop: "1px solid currentColor", opacity: 0.15, margin: "8px 0" }} />;
        if (!line.trim()) return <div key={i} style={{ height: 6 }} />;

        // Detect list prefix for indentation/display purposes
        const bulletMatch = line.match(/^(\s*)[*\-]\s+(.*)/);
        const numMatch    = line.match(/^(\s*)(\d+)\.\s+(.*)/);
        const indent      = bulletMatch ? bulletMatch[1].length * 8 : numMatch ? numMatch[1].length * 8 : 0;
        // The display text (strip list marker)
        const displayText = bulletMatch ? bulletMatch[2] : numMatch ? numMatch[3] : line;
        // The bullet symbol shown in the left gutter
        const bulletSymbol = numMatch ? `${numMatch[2]}.` : "·";

        // Annotatable line
        if (canAnnotate) {
          const ann    = annotations?.[i];
          const aColor = ann ? annColors[ann] : null;
          return (
            <div key={i}
              onClick={() => onAnnotate(section.key, i, cycleAnnotation(ann))}
              title={ann ? `Currently ${ann} — click to cycle` : "Click to mark as verified or disputed"}
              style={{
                display: "flex", gap: 8, paddingLeft: indent,
                cursor: "pointer", padding: "2px 6px", marginLeft: indent ? 0 : -6,
                borderRadius: 2,
                background: ann ? aColor + "10" : "transparent",
                transition: "background 0.15s",
                alignItems: "flex-start",
              }}
              onMouseEnter={(e) => { if (!ann) e.currentTarget.style.background = T.borderMid + "88"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = ann ? aColor + "10" : "transparent"; }}>
              <span style={{ flexShrink: 0, color: aColor || T.textDeep, fontSize: 12, marginTop: 3, minWidth: 14, fontFamily: "monospace", transition: "color 0.15s" }}>
                {ann ? annIcons[ann] : bulletSymbol}
              </span>
              <span style={{
                flex: 1,
                color: ann === "disputed" ? T.textDim : T.textMuted,
                textDecoration: ann === "disputed" ? "line-through" : "none",
                opacity: ann === "disputed" ? 0.55 : 1,
                transition: "all 0.2s",
              }}>
                {renderInline(displayText)}
              </span>
              {ann && (
                <span style={{ flexShrink: 0, fontSize: 9, fontFamily: "monospace", color: aColor, background: aColor + "18", border: `1px solid ${aColor}33`, padding: "1px 6px", letterSpacing: "0.1em", alignSelf: "center" }}>
                  {annLabels[ann]}
                </span>
              )}
            </div>
          );
        }

        // Non-annotatable — render normally
        if (bulletMatch) return (
          <div key={i} style={{ display: "flex", gap: 8, paddingLeft: indent }}>
            <span style={{ flexShrink: 0, opacity: 0.4 }}>·</span>
            <span>{renderInline(bulletMatch[2])}</span>
          </div>
        );
        if (numMatch) return <div key={i} style={{ paddingLeft: indent }}>{renderInline(line)}</div>;
        return <div key={i}>{renderInline(line)}</div>;
      })}
      {canAnnotate && (
        <div style={{ marginTop: 10, paddingTop: 8, borderTop: `1px solid ${T.border}`, fontSize: 10, fontFamily: "monospace", color: T.textGhost, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <span>Click any claim to annotate:</span>
          <span style={{ color: "#19C37D" }}>✓ verified</span>
          <span style={{ color: "#e05555" }}>✗ disputed</span>
          <span style={{ color: T.textGhost }}>click again to clear</span>
        </div>
      )}
    </div>
  );
}

// ─── Live Sources Panel ───────────────────────────────────────────────────────
function LiveSourcesPanel({ sources, searching, T }) {
  if (!searching && !sources?.length) return null;
  const verdictColor = (v) => {
    if (!v) return T.textFaint;
    const l = v.toLowerCase();
    if (l.includes("confirm")) return "#19C37D";
    if (l.includes("contradict")) return "#e05555";
    return "#E8C84A";
  };
  return (
    <div style={{ marginTop: 2, animation: "fadeIn 0.4s ease" }}>
      <div style={{ background: T.bgElevated, border: `1px solid ${T.border}`, borderLeft: "3px solid #4A8FD9" }}>
        <div style={{ padding: "12px 18px", display: "flex", alignItems: "center", gap: 8, borderBottom: `1px solid ${T.border}` }}>
          <span style={{ color: "#4A8FD9" }}>⊕</span>
          <span style={{ fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", fontFamily: "monospace", color: "#4A8FD9" }}>Live Web Sources</span>
          {searching && <span style={{ fontSize: 10, color: "#4A8FD9", fontFamily: "monospace", opacity: 0.6, animation: "pulse 1s infinite" }}>searching...</span>}
        </div>
        <div style={{ padding: "14px 18px" }}>
          {searching && !sources?.length && (
            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
              {[0, 1, 2].map((i) => <span key={i} style={{ display: "inline-block", width: 5, height: 5, borderRadius: "50%", background: "#4A8FD9", opacity: 0.4, animation: `bounce 1s ease-in-out ${i * 0.2}s infinite` }} />)}
            </div>
          )}
          {sources?.map((s, i) => (
            <div key={i} style={{ marginBottom: i < sources.length - 1 ? 14 : 0, paddingBottom: i < sources.length - 1 ? 14 : 0, borderBottom: i < sources.length - 1 ? `1px solid ${T.border}` : "none" }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10, marginBottom: 4 }}>
                {s.url
                  ? <a href={s.url} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: "#4A8FD9", fontFamily: "monospace", textDecoration: "none" }}>{s.title} →</a>
                  : <span style={{ fontSize: 13, color: T.textMuted, fontFamily: "monospace" }}>{s.title}</span>
                }
                {s.verdict && <span style={{ flexShrink: 0, fontSize: 10, fontFamily: "monospace", color: verdictColor(s.verdict), padding: "1px 8px", background: verdictColor(s.verdict) + "15", border: `1px solid ${verdictColor(s.verdict)}33`, letterSpacing: "0.08em" }}>{s.verdict}</span>}
              </div>
              {s.note && <p style={{ margin: 0, fontSize: 12, color: T.textMuted, lineHeight: 1.6, fontFamily: "Georgia, serif" }}>{s.note}</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Raw Responses Accordion ──────────────────────────────────────────────────
function RawResponsesAccordion({ responses, modelVersions, T }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginTop: 16 }}>
      <button onClick={() => setOpen(!open)} style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: T.bg, border: `1px solid ${T.border}`, color: T.textDim, fontSize: 11, fontFamily: "monospace", cursor: "pointer", letterSpacing: "0.1em", textTransform: "uppercase" }}>
        <span>Raw AI Responses</span>
        <span>{open ? "▲" : "▼"} {Object.keys(responses).length} responses</span>
      </button>
      {open && MODELS.filter((m) => responses[m.id]).map((m) => {
        const vDef = m.versions.find((v) => v.id === modelVersions?.[m.id]);
        return (
          <div key={m.id} style={{ border: `1px solid ${T.border}`, borderTop: "none" }}>
            <div style={{ padding: "8px 14px", background: T.bgElevated, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ color: m.color, fontSize: 10 }}>{m.icon}</span>
              <span style={{ fontSize: 11, fontFamily: "monospace", color: m.color, letterSpacing: "0.08em" }}>{m.label}</span>
              {vDef && <span style={{ fontSize: 9, fontFamily: "monospace", color: T.textFaint }}>{vDef.label}</span>}
            </div>
            <div style={{ padding: "12px 14px", fontSize: 12, lineHeight: 1.7, color: T.textMuted, whiteSpace: "pre-wrap", fontFamily: "Georgia, serif", maxHeight: 200, overflowY: "auto" }}>{responses[m.id]}</div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Consolidated Report Modal ────────────────────────────────────────────────
function ReportModal({ prompt, sections, analysis, responses, sources, modelVersions, timestamp, annotations, personaId, onClose, T }) {
  const [copied, setCopied] = useState(false);
  const exportText = buildExportText({ prompt, sections, responses, sources, modelVersions, timestamp, personaId, annotations });
  const confidenceSection = sections?.find((s) => s.key === "CONFIDENCE VERDICT");
  const confidenceLevel = confidenceSection ? extractConfidenceLevel(confidenceSection.content) : null;
  const persona = PERSONA_MODES.find((p) => p.id === personaId);

  const handleCopy = async () => {
    try { await mobileClipboardWrite(exportText); setCopied(true); setTimeout(() => setCopied(false), 2500); } catch {}
  };

  // Count annotations
  const totalAnnotations = annotations ? Object.values(annotations).reduce((sum, sAnn) => sum + Object.keys(sAnn).length, 0) : 0;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.88)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 24 }} onClick={onClose}>
      <div style={{ background: T.bgModal, border: `1px solid ${T.borderHi}`, width: "100%", maxWidth: 720, maxHeight: "90vh", display: "flex", flexDirection: "column", animation: "fadeIn 0.2s ease" }} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: "22px 28px 18px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: "0.2em", color: T.textFaint, textTransform: "uppercase", fontFamily: "monospace", marginBottom: 5 }}>Export · v7.1</div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 400, color: T.text }}>Consolidated Report</h2>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button onClick={handleCopy} style={{ padding: "8px 18px", background: copied ? "#0a1a0f" : T.accent, border: `1px solid ${copied ? "#19C37D33" : "transparent"}`, color: copied ? "#19C37D" : T.accentText, fontSize: 12, fontFamily: "monospace", cursor: "pointer", letterSpacing: "0.12em", textTransform: "uppercase", transition: "all 0.2s" }}>
              {copied ? "✓ Copied!" : "⎘ Copy All"}
            </button>
            <button onClick={onClose} style={{ background: "none", border: "none", color: T.textFaint, fontSize: 20, cursor: "pointer", lineHeight: 1 }}>✕</button>
          </div>
        </div>

        <div style={{ overflowY: "auto", padding: "24px 28px", flex: 1 }}>
          <div style={{ padding: "14px 18px", background: T.bg, border: `1px solid ${T.border}`, marginBottom: 16 }}>
            <div style={{ fontSize: 10, letterSpacing: "0.15em", color: T.textFaint, fontFamily: "monospace", textTransform: "uppercase", marginBottom: 8 }}>Question</div>
            <p style={{ margin: "0 0 12px", fontSize: 15, color: T.text, fontFamily: "Georgia, serif", lineHeight: 1.5 }}>{prompt}</p>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
              {confidenceLevel && <ConfidenceMeter level={confidenceLevel} />}
              {persona && persona.id !== "balanced" && (
                <span style={{ fontSize: 10, fontFamily: "monospace", color: persona.color, background: persona.color + "15", padding: "2px 8px", border: `1px solid ${persona.color}33` }}>
                  {persona.icon} {persona.label} Mode
                </span>
              )}
              {totalAnnotations > 0 && (
                <span style={{ fontSize: 10, fontFamily: "monospace", color: T.textFaint }}>
                  {totalAnnotations} claim{totalAnnotations !== 1 ? "s" : ""} annotated
                </span>
              )}
              <span style={{ fontSize: 11, color: T.textFaint, fontFamily: "monospace" }}>{formatTime(timestamp)}</span>
              <div style={{ display: "flex", gap: 6 }}>
                {MODELS.filter((m) => responses?.[m.id]).map((m) => {
                  const vDef = m.versions.find((v) => v.id === modelVersions?.[m.id]);
                  return <span key={m.id} style={{ fontSize: 10, fontFamily: "monospace", color: m.color, background: m.color + "15", padding: "2px 8px", border: `1px solid ${m.color}33` }}>{m.icon} {vDef?.label || m.label}</span>;
                })}
              </div>
            </div>
          </div>

          {sections && sections.map((sec) => {
            const sAnn = annotations?.[sec.key] || {};
            const hasAnn = Object.keys(sAnn).length > 0;
            return (
              <div key={sec.key} style={{ background: T.bgElevated, border: `1px solid ${T.border}`, borderLeft: `3px solid ${sec.color}`, marginBottom: 2 }}>
                <div style={{ padding: "10px 18px", display: "flex", alignItems: "center", gap: 8, borderBottom: `1px solid ${T.border}` }}>
                  <span style={{ color: sec.color }}>{sec.emoji}</span>
                  <span style={{ fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", fontFamily: "monospace", color: sec.color }}>{sec.label}</span>
                  {hasAnn && <span style={{ fontSize: 9, fontFamily: "monospace", color: T.textFaint, marginLeft: 4 }}>{Object.keys(sAnn).length} annotated</span>}
                </div>
                <div style={{ padding: "12px 18px", fontSize: 13, lineHeight: 1.8, color: T.textMuted, whiteSpace: "pre-wrap", fontFamily: "Georgia, serif" }}>{sec.content}</div>
              </div>
            );
          })}

          {sources?.length > 0 && (
            <>
              <div style={{ marginTop: 16, marginBottom: 8, fontSize: 10, letterSpacing: "0.15em", color: T.textFaint, fontFamily: "monospace", textTransform: "uppercase" }}>Web Sources</div>
              {sources.map((s, i) => {
                const vc = s.verdict?.toLowerCase().includes("confirm") ? "#19C37D" : s.verdict?.toLowerCase().includes("contradict") ? "#e05555" : "#E8C84A";
                return (
                  <div key={i} style={{ padding: "10px 14px", background: T.bgElevated, border: `1px solid ${T.border}`, marginBottom: 2, display: "flex", gap: 12, alignItems: "flex-start" }}>
                    <span style={{ fontSize: 11, fontFamily: "monospace", color: T.textFaint, flexShrink: 0, marginTop: 1 }}>{i + 1}.</span>
                    <div style={{ flex: 1 }}>
                      {s.url ? <a href={s.url} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: "#4A8FD9", fontFamily: "monospace", textDecoration: "none" }}>{s.title} →</a>
                               : <span style={{ fontSize: 13, color: T.textMuted, fontFamily: "monospace" }}>{s.title}</span>}
                      {s.note && <p style={{ margin: "4px 0 0", fontSize: 12, color: T.textMuted, lineHeight: 1.5 }}>{s.note}</p>}
                    </div>
                    {s.verdict && <span style={{ flexShrink: 0, fontSize: 10, fontFamily: "monospace", color: vc, padding: "2px 8px", background: vc + "15", border: `1px solid ${vc}33`, letterSpacing: "0.08em" }}>{s.verdict}</span>}
                  </div>
                );
              })}
            </>
          )}

          {responses && Object.keys(responses).length > 0 && <RawResponsesAccordion responses={responses} modelVersions={modelVersions} T={T} />}

          <div style={{ marginTop: 20 }}>
            <div style={{ fontSize: 10, letterSpacing: "0.15em", color: T.textFaint, fontFamily: "monospace", textTransform: "uppercase", marginBottom: 8 }}>Plain Text Preview</div>
            <pre style={{ margin: 0, padding: "16px", background: T.bg, border: `1px solid ${T.border}`, fontSize: 11, fontFamily: "monospace", color: T.textMuted, lineHeight: 1.7, overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
              {exportText}
            </pre>
          </div>
        </div>

        <div style={{ padding: "16px 28px", borderTop: `1px solid ${T.border}`, flexShrink: 0, display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button onClick={onClose} style={{ padding: "9px 20px", background: "transparent", border: `1px solid ${T.borderHi}`, color: T.textFaint, fontSize: 12, fontFamily: "monospace", cursor: "pointer", letterSpacing: "0.1em" }}>Close</button>
          <button onClick={handleCopy} style={{ padding: "9px 24px", background: copied ? "#0a1a0f" : T.accent, border: `1px solid ${copied ? "#19C37D33" : "transparent"}`, color: copied ? "#19C37D" : T.accentText, fontSize: 12, fontFamily: "monospace", cursor: "pointer", letterSpacing: "0.15em", textTransform: "uppercase", transition: "all 0.2s", minWidth: 120 }}>
            {copied ? "✓ Copied!" : "⎘ Copy All"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── History Comparison Modal (Feature 3) ────────────────────────────────────
function CompareModal({ itemA, itemB, onClose, T }) {
  const parseItem = (item) => ({
    sections: item.analysis ? parseAnalysis(item.analysis) : null,
    confidence: item.confidence,
    prompt: item.prompt,
    ts: item.ts,
    modelCount: item.modelCount,
    modelVersions: item.modelVersions || {},
  });

  const a = parseItem(itemA);
  const b = parseItem(itemB);

  const getSectionContent = (sections, key) => sections?.find((s) => s.key === key)?.content || "";

  const COMPARE_SECTIONS = ["CONSENSUS", "DISAGREEMENTS", "CONFIDENCE VERDICT"];

  const confidenceOrder = { HIGH: 3, MEDIUM: 2, LOW: 1 };
  const confDiff = (a.confidence && b.confidence) ? confidenceOrder[b.confidence] - confidenceOrder[a.confidence] : 0;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.9)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 24 }} onClick={onClose}>
      <div style={{ background: T.bgModal, border: `1px solid ${T.borderHi}`, width: "100%", maxWidth: 960, maxHeight: "90vh", display: "flex", flexDirection: "column", animation: "fadeIn 0.2s ease" }} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: "20px 28px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: "0.2em", color: T.textFaint, textTransform: "uppercase", fontFamily: "monospace", marginBottom: 4 }}>Feature 3 · History</div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 400, color: T.text }}>Side-by-Side Comparison</h2>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: T.textFaint, fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>

        <div style={{ overflowY: "auto", flex: 1, padding: "20px 28px" }}>

          {/* Column headers */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            {[itemA, itemB].map((item, idx) => (
              <div key={idx} style={{ padding: "12px 16px", background: T.bg, border: `1px solid ${T.border}`, borderTop: `3px solid ${idx === 0 ? "#D97B4A" : "#4A8FD9"}` }}>
                <div style={{ fontSize: 10, fontFamily: "monospace", color: idx === 0 ? "#D97B4A" : "#4A8FD9", letterSpacing: "0.15em", marginBottom: 6 }}>RUN {idx + 1} · {formatTime(item.ts)}</div>
                <p style={{ margin: "0 0 8px", fontSize: 13, color: T.text, fontFamily: "Georgia, serif", lineHeight: 1.4, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>{item.prompt}</p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  {item.confidence && <ConfidenceMeter level={item.confidence} />}
                  <span style={{ fontSize: 10, color: T.textGhost, fontFamily: "monospace" }}>{item.modelCount} model{item.modelCount !== 1 ? "s" : ""}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Confidence delta */}
          {confDiff !== 0 && (
            <div style={{ padding: "10px 16px", background: confDiff > 0 ? "#0a1a0f" : "#1a0a0a", border: `1px solid ${confDiff > 0 ? "#19C37D33" : "#e0555533"}`, marginBottom: 12, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 12, fontFamily: "monospace", color: confDiff > 0 ? "#19C37D" : "#e05555" }}>
                {confDiff > 0 ? "▲" : "▼"} Confidence {confDiff > 0 ? "increased" : "decreased"} from Run 1 to Run 2
              </span>
            </div>
          )}

          {/* Section comparisons */}
          {COMPARE_SECTIONS.map((secKey) => {
            const def = SECTION_DEFS.find((s) => s.key === secKey);
            const contentA = getSectionContent(a.sections, secKey);
            const contentB = getSectionContent(b.sections, secKey);
            const identical = contentA.trim() === contentB.trim();

            return (
              <div key={secKey} style={{ marginBottom: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <span style={{ color: def?.color }}>{def?.emoji}</span>
                  <span style={{ fontSize: 11, fontFamily: "monospace", color: def?.color, letterSpacing: "0.15em", textTransform: "uppercase" }}>{def?.label}</span>
                  {identical && <span style={{ fontSize: 9, fontFamily: "monospace", color: "#19C37D", background: "#0a1a0f", padding: "1px 6px", border: "1px solid #19C37D33" }}>identical</span>}
                  {!identical && contentA && contentB && <span style={{ fontSize: 9, fontFamily: "monospace", color: "#E8C84A", background: "#1a1700", padding: "1px 6px", border: "1px solid #E8C84A33" }}>differs</span>}
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  {[contentA, contentB].map((content, idx) => (
                    <div key={idx} style={{ padding: "12px 14px", background: T.bgElevated, border: `1px solid ${T.border}`, borderLeft: `2px solid ${idx === 0 ? "#D97B4A44" : "#4A8FD944"}`, minHeight: 60 }}>
                      {content
                        ? <div style={{ fontSize: 12, lineHeight: 1.7, color: T.textMuted, fontFamily: "Georgia, serif" }}>{renderMarkdown(content)}</div>
                        : <span style={{ fontSize: 11, color: T.textGhost, fontFamily: "monospace" }}>Not available</span>
                      }
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ padding: "14px 28px", borderTop: `1px solid ${T.border}`, flexShrink: 0, display: "flex", justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{ padding: "9px 24px", background: T.accent, border: "none", color: T.accentText, fontSize: 12, fontFamily: "monospace", cursor: "pointer", letterSpacing: "0.12em" }}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Key Setup Modal ──────────────────────────────────────────────────────────
// ─── Disclaimer Banner ────────────────────────────────────────────────────────
function DisclaimerBanner({ T }) {
  const [open, setOpen] = React.useState(() => {
    try { return localStorage.getItem("disclaimer_open") !== "0"; } catch { return true; }
  });

  const toggle = () => {
    const next = !open;
    setOpen(next);
    try { localStorage.setItem("disclaimer_open", next ? "1" : "0"); } catch {}
  };

  return (
    <div style={{ marginBottom: 20, background: T.isDark ? "#0d0d10" : "#f0f0f8", border: `1px solid ${T.isDark ? "#2a2a3a" : "#c8c4d8"}`, borderLeft: `3px solid #8888cc` }}>
      {/* Header row — always visible */}
      <div onClick={toggle} style={{ padding: "11px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer", userSelect: "none" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 10, fontFamily: "monospace", color: "#8888cc", letterSpacing: "0.18em", textTransform: "uppercase" }}>ⓘ Disclaimer & Privacy Notice</span>
        </div>
        <span style={{ fontSize: 10, color: "#8888cc", fontFamily: "monospace" }}>{open ? "▲ collapse" : "▼ read"}</span>
      </div>

      {/* Collapsible body */}
      {open && (
        <div style={{ padding: "0 18px 16px", animation: "fadeIn 0.2s ease" }}>
          <div style={{ paddingTop: 4, borderTop: `1px solid ${T.isDark ? "#2a2a3a" : "#c8c4d8"}` }}>
            <div style={{ fontSize: 10, fontFamily: "monospace", color: "#8888cc", letterSpacing: "0.18em", textTransform: "uppercase", margin: "12px 0 8px" }}>
              AI Accuracy
            </div>
            <p style={{ margin: "0 0 6px", fontSize: 12, fontFamily: "monospace", color: T.textDim, lineHeight: 1.65 }}>
              <strong style={{ color: T.textMuted }}>AI outputs are not guaranteed to be accurate.</strong>{" "}
              Responses from Claude, ChatGPT, and Gemini may contain errors, outdated information, or hallucinations — even when stated with confidence.
            </p>
            <p style={{ margin: 0, fontSize: 12, fontFamily: "monospace", color: T.textDim, lineHeight: 1.65 }}>
              <strong style={{ color: T.textMuted }}>This tool is a research aid, not a source of truth.</strong>{" "}
              The synthesized fact-check report reflects AI consensus only. Always verify important claims through authoritative primary sources before acting on or sharing any results.
            </p>

            <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${T.isDark ? "#2a2a3a" : "#c8c4d8"}` }}>
              <div style={{ fontSize: 10, fontFamily: "monospace", color: "#8888cc", letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 8 }}>
                Privacy & Data Storage
              </div>
              <p style={{ margin: "0 0 6px", fontSize: 12, fontFamily: "monospace", color: T.textDim, lineHeight: 1.65 }}>
                <strong style={{ color: T.textMuted }}>All data is stored locally on your device only.</strong>{" "}
                API keys, query history, usage logs, and cost data are saved in your browser's local storage and are never transmitted to any server operated by this app.
              </p>
              <p style={{ margin: 0, fontSize: 12, fontFamily: "monospace", color: T.textDim, lineHeight: 1.65 }}>
                API keys are sent directly and exclusively to their respective providers (Anthropic, OpenAI, Google) when a query is made — no third party sees them. Standard personal data due diligence applies: avoid entering sensitive personal information into prompts, and be cautious on shared devices.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Changelog Modal ──────────────────────────────────────────────────────────
const CHANGELOG = [
  {
    version: "v8.0",
    label: "Latest",
    labelColor: "#D97B4A",
    changes: [
      "Removed Grok (xAI) — engine now runs 3 AI models: Claude, ChatGPT, Gemini",
      "Redesigned response card layout for 3-model display",
      "Updated logo to 3-node triangle design",
      "Cleaned up localStorage migration for removed model keys",
    ],
  },
  {
    version: "v7.1",
    label: "",
    labelColor: "#D97B4A",
    changes: [
      "Extended Claude model list — added Haiku 3.5 and Haiku 3 for budget-friendly usage",
      "Bio link — footer now links to anaken.one",
    ],
  },
  {
    version: "v7.1",
    label: "",
    labelColor: "#D97B4A",
    changes: [
      "Renamed to Multi AI Fact-Check Engine",
      "Logo / avatar — three-node SVG icon representing each AI converging on synthesis",
      "Byline — one-line summary of how the tool works, shown in the header",
      "Setup instructions — expandable step-by-step key guide per provider inside the API Keys modal",
      "Disclaimer — AI accuracy warning and research-aid notice shown on first use",
      "Privacy notice — explains all data is stored locally, never sent to app servers",
      "Clear all data — one-tap wipe of history and usage logs from the privacy section",
      "Changelog modal — full release history accessible from the footer",
    ],
  },
  {
    version: "v6.0",
    changes: [
      "All-time costs & usage dashboard with trendlines",
      "Selectable time range for cost analytics",
      "Backup & restore — export/import your full history and usage data",
    ],
  },
  {
    version: "v5.0",
    changes: [
      "Cost & usage tracker — see per-model spend in real time",
      "Pricing fetch optimization — auto-refreshes rates in background",
    ],
  },
  {
    version: "v4.0",
    changes: [
      "Export button — download full fact-check reports",
      "Confidence meter — visual verdict strength indicator",
      "Bug fixes across streaming and annotation layers",
    ],
  },
  {
    version: "v3.0",
    changes: [
      "Selectable AI models — choose version per provider",
      "Progress bar — live status across all parallel calls",
      "History — save, reload and compare past fact-checks",
      "Theme toggle — dark / light mode",
    ],
  },
  {
    version: "v2.0",
    changes: [
      "Single prompt input — type once, all AIs respond automatically",
      "Parallel querying — Claude, ChatGPT, Gemini called simultaneously",
      "Live response cards — watch each AI respond in real time",
      "In-app key manager — add/update API keys inside the UI",
      "Smart skipping — models without keys are skipped gracefully",
    ],
  },
  {
    version: "v1.0",
    changes: ["Initial release"],
  },
];

function ChangelogModal({ onClose, T }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 24 }}>
      <div style={{ background: T.bgModal, border: `1px solid ${T.borderHi}`, width: "100%", maxWidth: 520, maxHeight: "88vh", overflowY: "auto" }}>
        <div style={{ padding: "22px 28px 18px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start", position: "sticky", top: 0, background: T.bgModal, zIndex: 1 }}>
          <div>
            <div style={{ fontSize: 10, fontFamily: "monospace", color: T.textFaint, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 5 }}>Release Notes</div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 400, color: T.text }}>Changelog</h2>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: T.textFaint, fontSize: 20, cursor: "pointer", lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ padding: "20px 28px 28px" }}>
          {CHANGELOG.map((entry, i) => (
            <div key={entry.version} style={{ marginBottom: i < CHANGELOG.length - 1 ? 24 : 0, paddingBottom: i < CHANGELOG.length - 1 ? 24 : 0, borderBottom: i < CHANGELOG.length - 1 ? `1px solid ${T.border}` : "none" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <span style={{ fontSize: 13, fontFamily: "monospace", color: T.text, fontWeight: 600, letterSpacing: "0.05em" }}>{entry.version}</span>
                {entry.label && (
                  <span style={{ fontSize: 9, fontFamily: "monospace", padding: "2px 8px", background: entry.labelColor + "18", color: entry.labelColor, border: `1px solid ${entry.labelColor}44`, letterSpacing: "0.1em", textTransform: "uppercase" }}>{entry.label}</span>
                )}
              </div>
              <ul style={{ margin: 0, padding: "0 0 0 16px", listStyle: "none" }}>
                {entry.changes.map((c, j) => (
                  <li key={j} style={{ fontSize: 13, color: T.textMuted, fontFamily: "Georgia, serif", lineHeight: 1.7, marginBottom: 3, display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <span style={{ color: T.textGhost, fontSize: 10, marginTop: 5, flexShrink: 0 }}>◆</span>
                    <span>{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Setup Instructions per model ─────────────────────────────────────────────
const SETUP_STEPS = {
  claude: {
    steps: [
      { text: "Go to", link: "https://console.anthropic.com", linkLabel: "console.anthropic.com" },
      { text: "Sign up / log in" },
      { text: "Click API Keys → Create Key" },
      { text: "Copy the key (starts with sk-ant-...)" },
    ],
    note: "⚠ Required for the synthesis/analysis step even if you have other keys",
    noteColor: "#D97B4A",
  },
  gemini: {
    steps: [
      { text: "Go to", link: "https://aistudio.google.com/app/apikey", linkLabel: "aistudio.google.com/app/apikey" },
      { text: "Sign in with a Google account" },
      { text: "Click Create API Key" },
      { text: "Copy the key (starts with AIza...)" },
    ],
  },
  chatgpt: {
    steps: [
      { text: "Go to", link: "https://platform.openai.com/api-keys", linkLabel: "platform.openai.com/api-keys" },
      { text: "Sign in and add a payment method" },
      { text: "Click + Create new secret key" },
      { text: "Copy the key (starts with sk-...)" },
    ],
    note: "$ Paid plan required to use the API",
    noteColor: "#e05555",
  },
};

function KeySetupModal({ onClose, savedKeys, onSave, selectedVersions, onSaveVersions, T }) {
  const [draft, setDraft]       = useState({ ...savedKeys });
  const [versions, setVersions] = useState({ ...getDefaultVersions(), ...selectedVersions });
  const [show, setShow]         = useState({});
  const [openInstructions, setOpenInstructions] = useState({});

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: 24 }}>
      <div style={{ background: T.bgModal, border: `1px solid ${T.borderHi}`, width: "100%", maxWidth: 580, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ padding: "24px 28px 20px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 11, fontFamily: "monospace", color: T.textFaint, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 6 }}>Configuration</div>
            <h2 style={{ margin: 0, fontSize: 20, fontWeight: 400, color: T.text }}>API Keys & Models</h2>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: T.textFaint, fontSize: 20, cursor: "pointer", lineHeight: 1 }}>✕</button>
        </div>

        <div style={{ padding: "20px 28px" }}>
          {MODELS.map((m) => {
            const hasKey = !!draft[m.id]?.trim();
            const currentVersion = versions[m.id] || m.defaultVersion;
            return (
              <div key={m.id} style={{ marginBottom: 28, paddingBottom: 28, borderBottom: `1px solid ${T.border}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ color: m.color, fontSize: 13 }}>{m.icon}</span>
                    <span style={{ fontSize: 14, fontFamily: "monospace", color: m.color, letterSpacing: "0.08em", fontWeight: 500 }}>{m.label}</span>
                    <span style={{ fontSize: 10, padding: "2px 7px", background: m.free ? "#0a1a0f" : "#1a0a0a", color: m.free ? "#19C37D" : "#e05555", border: `1px solid ${m.free ? "#0f2a18" : "#2a1010"}`, fontFamily: "monospace" }}>{m.freeNote}</span>
                  </div>
                  <button onClick={() => setOpenInstructions(prev => ({ ...prev, [m.id]: !prev[m.id] }))}
                    style={{ fontSize: 11, fontFamily: "monospace", color: T.textFaint, background: "none", border: "none", cursor: "pointer", letterSpacing: "0.05em", display: "flex", alignItems: "center", gap: 4 }}>
                    {openInstructions[m.id] ? "▲ Hide steps" : "▼ How to get key"}
                  </button>
                </div>
                {openInstructions[m.id] && (() => {
                  const steps = SETUP_STEPS[m.id];
                  if (!steps) return null;
                  return (
                    <div style={{ marginBottom: 12, padding: "12px 14px", background: m.color + "08", border: `1px solid ${m.color}22`, animation: "fadeIn 0.2s ease" }}>
                      <ol style={{ margin: 0, padding: "0 0 0 18px" }}>
                        {steps.steps.map((s, idx) => (
                          <li key={idx} style={{ fontSize: 12, fontFamily: "monospace", color: T.textDim, marginBottom: 6, lineHeight: 1.5 }}>
                            {s.text}
                            {s.link && (
                              <> <a href={s.link} target="_blank" rel="noreferrer" style={{ color: m.color, textDecoration: "none" }}>{s.linkLabel}</a></>
                            )}
                          </li>
                        ))}
                      </ol>
                      {steps.note && (
                        <div style={{ marginTop: 10, fontSize: 11, fontFamily: "monospace", color: steps.noteColor, lineHeight: 1.5, borderTop: `1px solid ${m.color}22`, paddingTop: 8 }}>
                          {steps.note}
                        </div>
                      )}
                    </div>
                  );
                })()}
                <div style={{ position: "relative", marginBottom: 10 }}>
                  <input type={show[m.id] ? "text" : "password"} value={draft[m.id] || ""} onChange={(e) => setDraft({ ...draft, [m.id]: e.target.value })} placeholder={m.keyHint}
                    style={{ width: "100%", background: T.bgInput, border: `1px solid ${hasKey ? m.color + "44" : T.borderMid}`, color: T.text, padding: "9px 44px 9px 14px", fontSize: 13, fontFamily: "monospace", outline: "none", boxSizing: "border-box" }} />
                  <button onClick={() => setShow({ ...show, [m.id]: !show[m.id] })} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", color: T.textGhost, cursor: "pointer", fontSize: 13 }}>
                    {show[m.id] ? "◉" : "○"}
                  </button>
                </div>
                <div>
                  <div style={{ fontSize: 10, letterSpacing: "0.12em", color: T.textGhost, fontFamily: "monospace", textTransform: "uppercase", marginBottom: 6 }}>Model version</div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {m.versions.map((v) => {
                      const selected = currentVersion === v.id;
                      return (
                        <button key={v.id} onClick={() => setVersions({ ...versions, [m.id]: v.id })}
                          style={{ padding: "6px 12px", background: selected ? m.color + "18" : T.bgInput, border: `1px solid ${selected ? m.color + "88" : T.borderMid}`, color: selected ? m.color : T.textDim, fontSize: 11, fontFamily: "monospace", cursor: "pointer", letterSpacing: "0.05em", transition: "all 0.15s" }}>
                          {v.label}
                          {v.note && <span style={{ fontSize: 9, color: selected ? m.color + "aa" : T.textGhost, marginLeft: 4 }}>· {v.note}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{ padding: "0 28px 28px" }}>
          <button onClick={() => { onSave(draft); onSaveVersions(versions); onClose(); }} style={{ width: "100%", padding: "14px", background: T.accent, color: T.accentText, border: "none", fontSize: 13, letterSpacing: "0.15em", textTransform: "uppercase", fontFamily: "monospace", cursor: "pointer" }}>
            ◈ Save Keys & Models
          </button>
          <p style={{ fontSize: 11, color: T.textGhost, fontFamily: "monospace", marginTop: 12, textAlign: "center", lineHeight: 1.6 }}>Keys stored in browser local storage — sent only to their respective AI providers, never to this app's servers.</p>
        </div>
      </div>
    </div>
  );
}

// ─── History Panel (Feature 3: Compare mode) ──────────────────────────────────
function HistoryPanel({ history, onLoad, onClear, onClose, onCompare, T }) {
  const [compareMode, setCompareMode] = useState(false);
  const [selected, setSelected]       = useState([]);

  const toggleSelect = (item) => {
    setSelected((prev) => {
      const idx = prev.findIndex((x) => x.id === item.id);
      if (idx >= 0) return prev.filter((x) => x.id !== item.id);
      if (prev.length >= 2) return [prev[1], item];
      return [...prev, item];
    });
  };

  const isSelected = (item) => selected.some((x) => x.id === item.id);

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "stretch", justifyContent: "flex-end", zIndex: 100 }} onClick={onClose}>
      <div style={{ background: T.bgElevated, borderLeft: `1px solid ${T.border}`, width: "100%", maxWidth: 420, overflowY: "auto", animation: "slideIn 0.25s ease" }} onClick={(e) => e.stopPropagation()}>

        <div style={{ padding: "24px 24px 18px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, background: T.bgElevated, zIndex: 1 }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: "0.2em", color: T.textFaint, textTransform: "uppercase", fontFamily: "monospace", marginBottom: 4 }}>Saved</div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 400, color: T.text }}>History</h2>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {history.length >= 2 && (
              <button onClick={() => { setCompareMode(!compareMode); setSelected([]); }}
                style={{ background: compareMode ? "#4A8FD918" : "none", border: `1px solid ${compareMode ? "#4A8FD9" : T.borderHi}`, color: compareMode ? "#4A8FD9" : T.textFaint, fontSize: 11, fontFamily: "monospace", padding: "5px 10px", cursor: "pointer", letterSpacing: "0.08em" }}>
                {compareMode ? "⊞ Comparing" : "⊞ Compare"}
              </button>
            )}
            {history.length > 0 && !compareMode && <button onClick={onClear} style={{ background: "none", border: `1px solid ${T.borderHi}`, color: T.textFaint, fontSize: 11, fontFamily: "monospace", padding: "5px 10px", cursor: "pointer", letterSpacing: "0.08em" }}>Clear all</button>}
            <button onClick={onClose} style={{ background: "none", border: "none", color: T.textFaint, fontSize: 20, cursor: "pointer" }}>✕</button>
          </div>
        </div>

        {/* Compare mode instructions */}
        {compareMode && (
          <div style={{ padding: "12px 24px", background: "#4A8FD910", borderBottom: `1px solid #4A8FD933` }}>
            <p style={{ margin: 0, fontSize: 12, fontFamily: "monospace", color: "#4A8FD9", lineHeight: 1.5 }}>
              {selected.length === 0 && "Select 2 runs to compare side-by-side →"}
              {selected.length === 1 && "Now select one more run →"}
              {selected.length === 2 && (
                <span>
                  2 runs selected —{" "}
                  <button onClick={() => { onCompare(selected[0], selected[1]); onClose(); }}
                    style={{ background: "#4A8FD9", border: "none", color: "#fff", fontSize: 11, fontFamily: "monospace", cursor: "pointer", padding: "2px 10px", letterSpacing: "0.08em" }}>
                    Compare now →
                  </button>
                </span>
              )}
            </p>
          </div>
        )}

        <div style={{ padding: "12px 0" }}>
          {history.length === 0 ? (
            <div style={{ padding: "40px 24px", textAlign: "center" }}>
              <div style={{ fontSize: 24, color: T.borderHi, marginBottom: 12 }}>◈</div>
              <p style={{ fontSize: 12, color: T.textGhost, fontFamily: "monospace", margin: 0, lineHeight: 1.6 }}>No checks yet. Run a fact-check<br />and it will appear here.</p>
            </div>
          ) : history.map((item) => {
            const sel = isSelected(item);
            const selIdx = selected.findIndex((x) => x.id === item.id);
            return (
              <button key={item.id}
                onClick={() => compareMode ? toggleSelect(item) : (onLoad(item), onClose())}
                style={{ display: "block", width: "100%", textAlign: "left", background: sel ? "#4A8FD918" : "none", border: "none", borderBottom: `1px solid ${T.border}`, borderLeft: sel ? "3px solid #4A8FD9" : "3px solid transparent", padding: "14px 24px", cursor: "pointer" }}
                onMouseEnter={(e) => { if (!sel) e.currentTarget.style.background = T.bg; }}
                onMouseLeave={(e) => { if (!sel) e.currentTarget.style.background = "none"; }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <p style={{ margin: "0 0 6px", fontSize: 13, color: T.text, fontFamily: "Georgia, serif", lineHeight: 1.4, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", flex: 1 }}>{item.prompt}</p>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                    {compareMode && sel && (
                      <span style={{ fontSize: 9, fontFamily: "monospace", color: "#4A8FD9", background: "#4A8FD918", border: "1px solid #4A8FD933", padding: "1px 6px" }}>
                        Run {selIdx + 1}
                      </span>
                    )}
                    {item.confidence && (
                      <span style={{ fontSize: 9, fontFamily: "monospace", padding: "2px 7px", color: item.confidence === "HIGH" ? "#19C37D" : item.confidence === "MEDIUM" ? "#E8C84A" : "#e05555", background: item.confidence === "HIGH" ? "#0a1a0f" : item.confidence === "MEDIUM" ? "#1a1700" : "#1a0a0a", border: `1px solid ${item.confidence === "HIGH" ? "#0f2a18" : item.confidence === "MEDIUM" ? "#2a2500" : "#2a1010"}`, letterSpacing: "0.1em" }}>
                        {item.confidence}
                      </span>
                    )}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <span style={{ fontSize: 10, color: T.textGhost, fontFamily: "monospace" }}>{formatTime(item.ts)}</span>
                  <span style={{ fontSize: 10, color: T.border, fontFamily: "monospace" }}>{item.modelCount} model{item.modelCount !== 1 ? "s" : ""}</span>
                  {item.personaId && item.personaId !== "balanced" && (
                    <span style={{ fontSize: 10, color: T.textGhost, fontFamily: "monospace" }}>
                      {PERSONA_MODES.find((p) => p.id === item.personaId)?.icon} {PERSONA_MODES.find((p) => p.id === item.personaId)?.label}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ─── Data Backup Modal ────────────────────────────────────────────────────────
function DataBackupModal({ usageLog, sessionTotals, history, pricing, pricingTs, onImport, onClose, onClearAll, T }) {
  const [importText, setImportText] = useState("");
  const [importError, setImportError] = useState(null);
  const [importSuccess, setImportSuccess] = useState(null);
  const [tab, setTab] = useState("export"); // "export" | "import"

  const exportData = () => ({
    _version: 1,
    exportedAt: new Date().toISOString(),
    pricing,
    pricingLastUpdated: pricingTs ? new Date(pricingTs).toISOString() : null,
    sessionTotals,
    usageLog,
    history,
  });

  const handleExportJSON = () => {
    const blob = new Blob([JSON.stringify(exportData(), null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `factcheck-backup-${Date.now()}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    if (!usageLog.length) return;
    const rows = [
      ["Timestamp", "Model", "Version", "Input Tokens", "Output Tokens", "Cost (USD)"],
      ...usageLog.map(e => [
        new Date(e.ts).toISOString(),
        e.modelLabel,
        e.version,
        e.inputTokens,
        e.outputTokens,
        (e.cost || 0).toFixed(6),
      ]),
    ];
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `factcheck-costs-${Date.now()}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileImport = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setImportText(ev.target.result);
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleImport = () => {
    setImportError(null); setImportSuccess(null);
    try {
      const parsed = JSON.parse(importText);
      if (parsed._version !== 1) throw new Error("Unrecognised backup format (expected _version: 1).");
      const result = {};
      if (Array.isArray(parsed.usageLog))  result.usageLog  = parsed.usageLog;
      if (Array.isArray(parsed.history))   result.history   = parsed.history;
      if (parsed.sessionTotals && typeof parsed.sessionTotals === "object") result.sessionTotals = parsed.sessionTotals;
      if (parsed.pricing && typeof parsed.pricing === "object")             result.pricing       = parsed.pricing;
      if (parsed.pricingLastUpdated) result.pricingTs = new Date(parsed.pricingLastUpdated).getTime();
      if (Object.keys(result).length === 0) throw new Error("Nothing importable found in file.");
      onImport(result);
      setImportSuccess(`Imported: ${result.history?.length || 0} history entries, ${result.usageLog?.length || 0} usage records.`);
      setImportText("");
    } catch (err) {
      setImportError(err.message);
    }
  };

  const btnStyle = (active) => ({
    padding: "7px 18px", background: active ? T.accent : "none",
    border: `1px solid ${active ? T.accent : T.borderHi}`,
    color: active ? T.accentText : T.textFaint,
    fontSize: 11, fontFamily: "monospace", cursor: "pointer", letterSpacing: "0.08em",
  });

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200 }} onClick={onClose}>
      <div style={{ background: T.bgModal, border: `1px solid ${T.borderMid}`, borderTop: `2px solid #19C37D`, width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto", animation: "fadeIn 0.2s ease" }} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 9, fontFamily: "monospace", color: T.textGhost, letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: 4 }}>Data Management</div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 400, color: T.text }}>Backup & Restore</h2>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: T.textFaint, fontSize: 20, cursor: "pointer" }}>✕</button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 0, padding: "16px 24px 0", borderBottom: `1px solid ${T.border}` }}>
          {["export", "import"].map(t => (
            <button key={t} onClick={() => setTab(t)}
              style={{ ...btnStyle(tab === t), marginRight: 6, marginBottom: -1, borderBottom: tab === t ? `1px solid ${T.bgModal}` : `1px solid ${T.borderHi}` }}>
              {t === "export" ? "⤓ Export / Backup" : "⤒ Import / Restore"}
            </button>
          ))}
        </div>

        <div style={{ padding: "20px 24px" }}>

          {tab === "export" && (
            <>
              {/* Stats */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 20 }}>
                {[
                  { label: "History Entries",  value: history.length },
                  { label: "Usage Log Records", value: usageLog.length },
                  { label: "Total Cost Tracked", value: formatCost(usageLog.reduce((s, e) => s + (e.cost || 0), 0)) },
                  { label: "Pricing Version",   value: pricingTs ? new Date(pricingTs).toLocaleDateString() : "Default" },
                ].map((s, i) => (
                  <div key={i} style={{ padding: "10px 14px", background: T.bg, border: `1px solid ${T.border}` }}>
                    <div style={{ fontSize: 9, fontFamily: "monospace", color: T.textGhost, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 4 }}>{s.label}</div>
                    <div style={{ fontSize: 15, fontFamily: "monospace", color: T.text, fontWeight: 600 }}>{s.value}</div>
                  </div>
                ))}
              </div>

              {/* Export buttons */}
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <button onClick={handleExportJSON}
                  style={{ padding: "12px 16px", background: T.bgElevated, border: `1px solid ${T.borderHi}`, color: T.text, fontSize: 12, fontFamily: "monospace", cursor: "pointer", letterSpacing: "0.08em", textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 3 }}>⤓ Full Backup (.json)</div>
                    <div style={{ fontSize: 10, color: T.textGhost }}>History, usage log, session totals, pricing — fully restorable</div>
                  </div>
                  <span style={{ color: "#19C37D", fontSize: 10, fontFamily: "monospace" }}>JSON</span>
                </button>

                <button onClick={handleExportCSV} disabled={!usageLog.length}
                  style={{ padding: "12px 16px", background: T.bgElevated, border: `1px solid ${usageLog.length ? T.borderHi : T.border}`, color: usageLog.length ? T.text : T.textGhost, fontSize: 12, fontFamily: "monospace", cursor: usageLog.length ? "pointer" : "not-allowed", letterSpacing: "0.08em", textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 3 }}>⤓ Cost Log (.csv)</div>
                    <div style={{ fontSize: 10, color: T.textGhost }}>All API calls with timestamps, tokens, and costs for spreadsheets</div>
                  </div>
                  <span style={{ color: "#E8C84A", fontSize: 10, fontFamily: "monospace" }}>CSV</span>
                </button>

                {/* Danger zone */}
                <div style={{ marginTop: 8, paddingTop: 16, borderTop: `1px solid ${T.border}` }}>
                  <div style={{ fontSize: 9, fontFamily: "monospace", color: T.textGhost, letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 10 }}>Danger Zone</div>
                  <button onClick={onClearAll}
                    style={{ width: "100%", padding: "12px 16px", background: "none", border: `1px solid #e0555544`, color: "#e05555", fontSize: 12, fontFamily: "monospace", cursor: "pointer", letterSpacing: "0.08em", textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#e0555510"}
                    onMouseLeave={e => e.currentTarget.style.background = "none"}>
                    <div>
                      <div style={{ fontWeight: 600, marginBottom: 3 }}>✕ Clear all history &amp; usage data</div>
                      <div style={{ fontSize: 10, color: "#e0555588" }}>Permanently deletes query history and cost logs — API keys are kept</div>
                    </div>
                  </button>
                </div>
              </div>
            </>
          )}

          {tab === "import" && (
            <>
              <p style={{ margin: "0 0 16px", fontSize: 12, fontFamily: "monospace", color: T.textGhost, lineHeight: 1.7 }}>
                Paste a JSON backup or use the file picker. Importing will <strong style={{ color: T.text }}>merge</strong> with existing data — duplicates are overwritten by the imported version.
              </p>

              {/* File picker */}
              <label style={{ display: "block", padding: "10px 14px", background: T.bg, border: `1px dashed ${T.borderHi}`, color: T.textFaint, fontSize: 11, fontFamily: "monospace", cursor: "pointer", marginBottom: 12, textAlign: "center", letterSpacing: "0.06em" }}>
                ⤒ Click to choose .json backup file
                <input type="file" accept=".json,application/json" onChange={handleFileImport} style={{ display: "none" }} />
              </label>

              {/* Paste area */}
              <textarea
                value={importText}
                onChange={e => { setImportText(e.target.value); setImportError(null); setImportSuccess(null); }}
                placeholder='Or paste JSON backup here…'
                rows={7}
                style={{ width: "100%", background: T.bgInput, border: `1px solid ${importError ? "#e05555" : T.borderMid}`, color: T.text, padding: "12px 14px", fontSize: 11, fontFamily: "monospace", resize: "vertical", boxSizing: "border-box", lineHeight: 1.5 }}
              />

              {importError   && <div style={{ padding: "8px 12px", background: "#1a0a0a", border: "1px solid #e0555533", color: "#e05555", fontSize: 11, fontFamily: "monospace", marginTop: 8 }}>⚠ {importError}</div>}
              {importSuccess && <div style={{ padding: "8px 12px", background: "#0a1a0f", border: "1px solid #19C37D33", color: "#19C37D", fontSize: 11, fontFamily: "monospace", marginTop: 8 }}>✓ {importSuccess}</div>}

              <button onClick={handleImport} disabled={!importText.trim()}
                style={{ marginTop: 14, width: "100%", padding: "13px", background: importText.trim() ? "#19C37D" : T.border, border: "none", color: importText.trim() ? "#0a0a0a" : T.textGhost, fontSize: 12, fontFamily: "monospace", cursor: importText.trim() ? "pointer" : "not-allowed", letterSpacing: "0.12em", textTransform: "uppercase" }}>
                ⤒ Import & Restore
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function App() {
  const [isDark, setIsDark]               = useState(loadDark);
  const T = makeTheme(isDark);

  const [keys, setKeys]                   = useState(loadKeys);
  const [selectedVersions, setSelectedVersions] = useState(() => ({ ...getDefaultVersions(), ...loadVersions() }));
  const [selectedPersona, setSelectedPersona]   = useState(loadPersona);
  const [showModal, setShowModal]         = useState(false);
  const [showHistory, setShowHistory]     = useState(false);
  const [showReport, setShowReport]       = useState(false);
  const [showCompare, setShowCompare]     = useState(false);
  const [showBackup, setShowBackup]       = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);
  const [showTutorial, setShowTutorial]   = useState(() => {
    try { return localStorage.getItem(TUTORIAL_STORAGE_KEY) !== "1"; } catch { return true; }
  });
  const [compareItems, setCompareItems]   = useState(null); // [itemA, itemB]
  const [history, setHistory]             = useState(loadHistory);
  const [prompt, setPrompt]               = useState("");
  const [statuses, setStatuses]           = useState({});
  const [responses, setResponses]         = useState({});
  const [errors, setErrors]               = useState({});
  const [analysis, setAnalysis]           = useState(null);
  const [analyzing, setAnalyzing]         = useState(false);
  const [alignment, setAlignment]         = useState({});
  const [sources, setSources]             = useState(null);
  const [searching, setSearching]         = useState(false);
  const [reportTimestamp, setReportTimestamp] = useState(null);
  const [annotations, setAnnotations]     = useState({}); // Feature 2
  const [needsReanalysis, setNeedsReanalysis] = useState(false); // Feature 1
  // Usage tracking
  const sessionStartTs                    = useRef(Date.now()).current;
  const [usageLog, setUsageLog]           = useState(loadUsageLog);
  const [sessionTotals, setSessionTotals] = useState(loadSessionTotals);
  // Pricing
  const [pricing, setPricing]             = useState(loadPricing);
  const [pricingTs, setPricingTs]         = useState(loadPricingTs);
  const [pricingLoading, setPricingLoading] = useState(false);
  const responsesRef   = useRef({});
  const analyzingRef   = useRef(false);
  const analysisRanRef = useRef(false); // prevent double-run

  const configuredCount = MODELS.filter((m) => keys[m.id]?.trim()).length;
  const toggleDark = () => setIsDark((d) => { saveDark(!d); return !d; });

  const handleClearAllData = useCallback(() => {
    if (!mobileConfirm("Clear all stored data?\n\nThis will permanently delete your query history, usage logs, and cost data. Your API keys will NOT be removed.\n\nThis cannot be undone.")) return;
    setHistory([]); saveHistory([]);
    setUsageLog([]); saveUsageLog([]);
    setSessionTotals({}); saveSessionTotals({});
    try { localStorage.removeItem("disclaimer_open"); } catch {}
  }, []);

  // Feature 2: annotation handler
  const handleAnnotate = useCallback((sectionKey, lineIndex, value) => {
    setAnnotations((prev) => {
      const updated = { ...prev, [sectionKey]: { ...(prev[sectionKey] || {}) } };
      if (value === null) {
        delete updated[sectionKey][lineIndex];
      } else {
        updated[sectionKey][lineIndex] = value;
      }
      return updated;
    });
  }, []);

  // Usage tracking helper
  const recordUsage = useCallback((modelId, versionId, inputTokens, outputTokens) => {
    const model = MODELS.find((m) => m.id === modelId);
    const vDef  = model?.versions.find((v) => v.id === versionId);
    const cost  = calcCost(versionId, inputTokens, outputTokens, pricing);
    const entry = {
      modelId, modelLabel: model?.label || modelId,
      version: vDef?.label || versionId,
      inputTokens, outputTokens, cost, ts: Date.now(),
    };
    setUsageLog((prev) => { const next = [...prev, entry]; saveUsageLog(next); return next; });
    setSessionTotals((prev) => {
      const cur = prev[modelId] || { calls: 0, inputTokens: 0, outputTokens: 0, cost: 0 };
      const next = {
        ...prev,
        [modelId]: {
          calls:        cur.calls + 1,
          inputTokens:  cur.inputTokens  + inputTokens,
          outputTokens: cur.outputTokens + outputTokens,
          cost:         cur.cost         + (cost || 0),
        },
      };
      saveSessionTotals(next);
      return next;
    });
  }, [pricing]);

  // Reset pricing to hardcoded defaults
  const handleResetPricing = useCallback(() => {
    const now = Date.now();
    savePricing(DEFAULT_PRICING, now);
    setPricing(DEFAULT_PRICING);
    setPricingTs(now);
  }, []);

  // Fetch latest pricing via Claude web search
  const handleRefreshPricing = useCallback(async () => {
    const claudeKey = keys["claude"]?.trim();
    if (!claudeKey) {
      alert("A Claude API key is required to fetch live pricing. Please add one in Settings.");
      return;
    }
    setPricingLoading(true);
    try {
      const modelIds = Object.keys(DEFAULT_PRICING).join(", ");

      const pricingPrompt = `Search anthropic.com/pricing, openai.com/api/pricing, and ai.google.dev/pricing for current per-token prices. Return ONLY a JSON object like {"model-id":{"in":N,"out":N},...} for these models: ${modelIds}. No markdown, no explanation.`;

      // Direct single-shot fetch — no looping, low token budget
      const res = await fetch("https://api.anthropic.com/v1/messages", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-api-key": claudeKey,
          "anthropic-version": "2023-06-01",
          "anthropic-dangerous-direct-browser-access": "true",
        },
        body: JSON.stringify({
          model: "claude-sonnet-4-6",
          max_tokens: 512,
          tools: [{ type: "web_search_20250305", name: "web_search" }],
          messages: [{ role: "user", content: pricingPrompt }],
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error?.message || "Claude API error");
      }
      const data = await res.json();
      const raw = data.content.filter((b) => b.type === "text").map((b) => b.text).join("\n");

      // Strip any accidental markdown fences
      const clean = raw.replace(/```json|```/gi, "").trim();
      // Extract the first {...} block
      const match = clean.match(/\{[\s\S]*\}/);
      if (!match) throw new Error("No JSON object found in response");

      const parsed = JSON.parse(match[0]);

      // Validate: must have all keys from DEFAULT_PRICING and numeric values
      const validated = { ...DEFAULT_PRICING };
      for (const [id, val] of Object.entries(parsed)) {
        if (
          id in DEFAULT_PRICING &&
          typeof val?.in === "number" && val.in > 0 &&
          typeof val?.out === "number" && val.out > 0
        ) {
          validated[id] = { in: val.in, out: val.out };
        }
      }

      const now = Date.now();
      savePricing(validated, now);
      setPricing(validated);
      setPricingTs(now);
    } catch (err) {
      console.error("Pricing refresh failed:", err);
      alert(`Failed to fetch live pricing: ${err.message}\n\nFalling back to built-in defaults.`);
      // Fall back to defaults so the timestamp still updates
      const now = Date.now();
      savePricing(DEFAULT_PRICING, now);
      setPricing(DEFAULT_PRICING);
      setPricingTs(now);
    } finally {
      setPricingLoading(false);
    }
  }, [keys]);

  const setModelState = (id, status, response = null, error = null) => {
    setStatuses((s) => ({ ...s, [id]: status }));
    if (response !== null) {
      responsesRef.current[id] = response;
      setResponses((r) => ({ ...r, [id]: response }));
    }
    if (error !== null) setErrors((e) => ({ ...e, [id]: error }));
  };

  // Streaming query for all models
  const handleQuery = async () => {
    if (!prompt.trim()) return;
    setAnalysis(null); setAlignment({}); setSources(null);
    setResponses({}); setErrors({}); setAnnotations({}); setNeedsReanalysis(false);
    responsesRef.current = {};
    analyzingRef.current = false;
    analysisRanRef.current = false;

    const initialStatuses = {};
    MODELS.forEach((m) => { initialStatuses[m.id] = keys[m.id]?.trim() ? "loading" : "skipped"; });
    setStatuses(initialStatuses);

    const activeModels = MODELS.filter((m) => keys[m.id]?.trim());
    const persona = PERSONA_MODES.find((p) => p.id === selectedPersona);

    activeModels.forEach((m) => {
      STREAM_CALLERS[m.id](
        prompt,
        keys[m.id].trim(),
        selectedVersions[m.id],
        (partial) => {
          responsesRef.current[m.id] = partial;
          setStatuses((s) => ({ ...s, [m.id]: "loading" }));
          setResponses((r) => ({ ...r, [m.id]: partial }));
        },
        (full, usage) => {
          responsesRef.current[m.id] = full;
          if (usage) recordUsage(m.id, selectedVersions[m.id], usage.inputTokens, usage.outputTokens);
          setStatuses((s) => {
            const next = { ...s, [m.id]: "done" };
            // Trigger analysis check after status update
            setTimeout(() => checkAndRunAnalysis(next, persona), 0);
            return next;
          });
          setResponses((r) => ({ ...r, [m.id]: full }));
        },
        (err) => {
          setStatuses((s) => ({ ...s, [m.id]: "error" }));
          setErrors((e) => ({ ...e, [m.id]: err }));
        }
      );
    });
  };

  const checkAndRunAnalysis = (currentStatuses, persona) => {
    if (analyzingRef.current || analysisRanRef.current) return;
    const done = MODELS.filter((m) => currentStatuses[m.id] === "done");
    const stillLoading = MODELS.some((m) => currentStatuses[m.id] === "loading");
    if (done.length >= 2 && !stillLoading) {
      analysisRanRef.current = true;
      runAnalysis(done.map((m) => ({ label: m.label, text: responsesRef.current[m.id] })), persona);
    }
  };

  const runAnalysis = async (filledResponses, persona) => {
    if (analyzingRef.current) return;
    analyzingRef.current = true;
    setAnalyzing(true);
    setAnnotations({});
    const ts = Date.now();
    setReportTimestamp(ts);
    try {
      const claudeKey = keys["claude"]?.trim();
      if (!claudeKey) throw new Error("Claude key needed for analysis synthesis");
      const personaNote = persona?.systemNote || "";
      const result = await callClaude(ANALYSIS_PROMPT(filledResponses, personaNote), claudeKey, selectedVersions["claude"]);
      setAnalysis(result);
      setAlignment(parseModelAlignment(result));
      setNeedsReanalysis(false);

      const confidence = extractConfidenceLevel(result);
      const newItem = {
        id: ts, ts, prompt, analysis: result,
        responses: { ...responsesRef.current },
        confidence, modelCount: filledResponses.length,
        modelVersions: { ...selectedVersions },
        personaId: selectedPersona,
      };
      setHistory((h) => {
        const updated = [newItem, ...h].slice(0, 50);
        saveHistory(updated);
        return updated;
      });

      const consensusSection = result.match(/\*\*CONSENSUS\*\*([\s\S]*?)(?=\*\*[A-Z]|$)/i);
      if (consensusSection) runGrounding(claudeKey, consensusSection[1].trim());
    } catch (e) {
      setAnalysis("⚠ Could not run synthesis: " + e.message);
    } finally {
      setAnalyzing(false);
      analyzingRef.current = false;
    }
  };

  const runGrounding = async (claudeKey, consensusClaims) => {
    setSearching(true); setSources([]);
    try { setSources(parseSources(await callClaudeWithWebSearch(GROUNDING_PROMPT(prompt, consensusClaims), claudeKey))); }
    catch { setSources([]); }
    finally { setSearching(false); }
  };

  // Feature 1: Per-model retry
  const handleRetry = useCallback((modelId) => {
    const model = MODELS.find((m) => m.id === modelId);
    if (!model || !keys[modelId]?.trim()) return;

    // Clear the model's state and restart streaming
    delete responsesRef.current[modelId];
    setResponses((r) => { const next = { ...r }; delete next[modelId]; return next; });
    setErrors((e) => { const next = { ...e }; delete next[modelId]; return next; });
    setStatuses((s) => ({ ...s, [modelId]: "loading" }));

    STREAM_CALLERS[modelId](
      prompt,
      keys[modelId].trim(),
      selectedVersions[modelId],
      (partial) => {
        responsesRef.current[modelId] = partial;
        setResponses((r) => ({ ...r, [modelId]: partial }));
      },
      (full, usage) => {
        responsesRef.current[modelId] = full;
        if (usage) recordUsage(modelId, selectedVersions[modelId], usage.inputTokens, usage.outputTokens);
        setResponses((r) => ({ ...r, [modelId]: full }));
        setStatuses((s) => ({ ...s, [modelId]: "done" }));
        setNeedsReanalysis(true);
      },
      (err) => {
        setStatuses((s) => ({ ...s, [modelId]: "error" }));
        setErrors((e) => ({ ...e, [modelId]: err }));
      }
    );
  }, [prompt, keys, selectedVersions]);

  const handleReanalyze = () => {
    const done = MODELS.filter((m) => statuses[m.id] === "done");
    if (done.length < 2) return;
    analysisRanRef.current = false;
    const persona = PERSONA_MODES.find((p) => p.id === selectedPersona);
    runAnalysis(done.map((m) => ({ label: m.label, text: responsesRef.current[m.id] })), persona);
  };

  const handleLoadHistory = (item) => {
    setPrompt(item.prompt);
    setAnalysis(item.analysis);
    setResponses(item.responses || {});
    setSources(null);
    setAlignment(item.analysis ? parseModelAlignment(item.analysis) : {});
    setReportTimestamp(item.ts);
    setAnnotations({});
    setNeedsReanalysis(false);
    responsesRef.current = item.responses || {};
    const newStatuses = {};
    MODELS.forEach((m) => { newStatuses[m.id] = item.responses?.[m.id] ? "done" : "skipped"; });
    setStatuses(newStatuses);
    if (item.modelVersions) setSelectedVersions((v) => ({ ...v, ...item.modelVersions }));
    if (item.personaId) setSelectedPersona(item.personaId);
  };

  const handlePersonaChange = (personaId) => {
    setSelectedPersona(personaId);
    savePersona(personaId);
  };

  const handleImportData = useCallback((result) => {
    if (result.usageLog) {
      const merged = [...loadUsageLog()];
      const existingTs = new Set(merged.map(e => e.ts + e.modelId));
      for (const e of result.usageLog) {
        if (!existingTs.has(e.ts + e.modelId)) merged.push(e);
      }
      merged.sort((a, b) => a.ts - b.ts);
      saveUsageLog(merged);
      setUsageLog(merged);
    }
    if (result.history) {
      const merged = [...loadHistory()];
      const existingIds = new Set(merged.map(h => h.id));
      for (const h of result.history) {
        if (!existingIds.has(h.id)) merged.push(h);
      }
      merged.sort((a, b) => b.ts - a.ts);
      saveHistory(merged);
      setHistory(merged);
    }
    if (result.sessionTotals) {
      saveSessionTotals(result.sessionTotals);
      setSessionTotals(result.sessionTotals);
    }
    if (result.pricing) {
      const ts = result.pricingTs || Date.now();
      savePricing(result.pricing, ts);
      setPricing(result.pricing);
      setPricingTs(ts);
    }
  }, []);

  const handleCompare = (itemA, itemB) => {
    setCompareItems([itemA, itemB]);
    setShowCompare(true);
  };

  const sections = analysis ? parseAnalysis(analysis) : null;
  const anyRunning = MODELS.some((m) => statuses[m.id] === "loading");
  const confidenceSection = sections?.find((s) => s.key === "CONFIDENCE VERDICT");
  const confidenceLevel = confidenceSection ? extractConfidenceLevel(confidenceSection.content) : null;
  const hasReport = !!analysis;
  const currentPersona = PERSONA_MODES.find((p) => p.id === selectedPersona) || PERSONA_MODES[0];
  const [responsesOpen, setResponsesOpen] = useState(true);

  return (
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: "Georgia, serif", color: T.text, transition: "background 0.3s, color 0.3s" }}>
      <style>{`
        @keyframes bounce   { 0%, 100% { transform: translateY(0); opacity: 0.4; } 50% { transform: translateY(-4px); opacity: 1; } }
        @keyframes fadeIn   { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideIn  { from { transform: translateX(40px); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
        @keyframes shimmer  { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
        @keyframes pulse    { 0%, 100% { opacity: 0.5; } 50% { opacity: 1; } }
        textarea:focus { outline: none; } input:focus { outline: none; } a:hover { opacity: 0.8; } button:focus { outline: none; }
        ::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-track { background: ${T.bg}; } ::-webkit-scrollbar-thumb { background: ${T.borderHi}; }
      `}</style>

      {showModal    && <KeySetupModal savedKeys={keys} onSave={(k) => { setKeys(k); saveKeys(k); }} selectedVersions={selectedVersions} onSaveVersions={(v) => { setSelectedVersions(v); saveVersions(v); }} onClose={() => setShowModal(false)} T={T} />}
      {showHistory  && <HistoryPanel history={history} onLoad={handleLoadHistory} onClear={() => { setHistory([]); saveHistory([]); }} onClose={() => setShowHistory(false)} onCompare={handleCompare} T={T} />}
      {showReport   && <ReportModal prompt={prompt} sections={sections} analysis={analysis} responses={responses} sources={sources} modelVersions={selectedVersions} timestamp={reportTimestamp || Date.now()} annotations={annotations} personaId={selectedPersona} onClose={() => setShowReport(false)} T={T} />}
      {showCompare  && compareItems && <CompareModal itemA={compareItems[0]} itemB={compareItems[1]} onClose={() => setShowCompare(false)} T={T} />}
      {showBackup   && <DataBackupModal usageLog={usageLog} sessionTotals={sessionTotals} history={history} pricing={pricing} pricingTs={pricingTs} onImport={handleImportData} onClose={() => setShowBackup(false)} onClearAll={handleClearAllData} T={T} />}
      {showChangelog && <ChangelogModal onClose={() => setShowChangelog(false)} T={T} />}
      {showTutorial && <TutorialOverlay T={T} onComplete={() => {
        setShowTutorial(false);
        try { localStorage.setItem(TUTORIAL_STORAGE_KEY, "1"); } catch {}
      }} />}

      {/* Header */}
      <div style={{ borderBottom: `1px solid ${T.border}`, background: T.headerBg }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "28px 40px 22px", display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 18 }}>
            {/* Logo */}
            <div style={{ flexShrink: 0 }}>
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="48" height="48" rx="10" fill={T.isDark ? "#111" : "#e8e4de"} stroke={T.borderHi} strokeWidth="1"/>
                {/* Three AI nodes — triangle arrangement */}
                <circle cx="24" cy="12" r="4" fill="#D97B4A" opacity="0.9"/>
                <circle cx="12" cy="34" r="4" fill="#19C37D" opacity="0.9"/>
                <circle cx="36" cy="34" r="4" fill="#4A8FD9" opacity="0.9"/>
                {/* Lines to center */}
                <line x1="24" y1="12" x2="24" y2="24" stroke={T.borderHi} strokeWidth="1.2" opacity="0.6"/>
                <line x1="12" y1="34" x2="24" y2="24" stroke={T.borderHi} strokeWidth="1.2" opacity="0.6"/>
                <line x1="36" y1="34" x2="24" y2="24" stroke={T.borderHi} strokeWidth="1.2" opacity="0.6"/>
                {/* Center synthesis node */}
                <circle cx="24" cy="24" r="5" fill="#D97B4A" opacity="0.95"/>
                <text x="24" y="28" textAnchor="middle" fontSize="6" fill="#fff" fontFamily="monospace">◈</text>
              </svg>
            </div>
            <div>
              <div style={{ fontSize: 10, letterSpacing: "0.25em", color: T.textFaint, textTransform: "uppercase", fontFamily: "monospace", marginBottom: 4 }}>
                Munki Business <span style={{ opacity: 0.45 }}>by</span> Alee
              </div>
              <h1 style={{ fontSize: 34, fontWeight: 400, margin: "0 0 5px", letterSpacing: "-0.02em", color: T.text }}>Multi AI Fact-Check Engine</h1>
              <p style={{ margin: 0, fontSize: 12, color: T.textDim, fontFamily: "monospace", letterSpacing: "0.03em", lineHeight: 1.5 }}>
                Type one question → all 3 AIs answer in parallel → Claude synthesizes a unified fact-check report.
              </p>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
            <button onClick={toggleDark}
              style={{ padding: "0 14px", background: "transparent", border: `1px solid ${T.borderHi}`, color: T.textDim, fontSize: 14, cursor: "pointer", lineHeight: 1, display: "flex", alignItems: "center" }}
              title={isDark ? "Switch to light" : "Switch to dark"}>
              {isDark ? "☀" : "☾"}
            </button>
            <button onClick={() => setShowModal(true)}
              style={{ padding: "0 18px", height: 36, background: "transparent", border: `1px solid ${configuredCount > 0 ? T.borderHi : "#3a2a1a"}`, color: configuredCount > 0 ? T.textDim : "#D97B4A", fontSize: 12, fontFamily: "monospace", cursor: "pointer", letterSpacing: "0.1em", display: "flex", alignItems: "center", gap: 8 }}>
              <span>⚙</span> APIs
              <span style={{ padding: "1px 7px", fontSize: 10, background: configuredCount > 0 ? T.border : "#1a0f0a", color: configuredCount > 0 ? T.textDim : "#D97B4A" }}>{configuredCount}/{MODELS.length}</span>
            </button>
            <button onClick={() => setShowHistory(true)}
              style={{ padding: "0 18px", height: 36, background: "transparent", border: `1px solid ${T.borderHi}`, color: history.length > 0 ? T.textDim : T.textGhost, fontSize: 12, fontFamily: "monospace", cursor: "pointer", letterSpacing: "0.1em", display: "flex", alignItems: "center", gap: 8 }}>
              <span>◷</span> History
              {history.length > 0 && <span style={{ padding: "1px 7px", fontSize: 10, background: T.border, color: T.textDim }}>{history.length}</span>}
            </button>
            <button onClick={() => setShowBackup(true)}
              style={{ padding: "0 18px", height: 36, background: "transparent", border: `1px solid ${T.borderHi}`, color: T.textDim, fontSize: 12, fontFamily: "monospace", cursor: "pointer", letterSpacing: "0.1em", display: "flex", alignItems: "center", gap: 8 }}
              title="Backup & restore your history and cost data">
              <span>⤓</span> Backup
            </button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "32px 40px" }}>

        {configuredCount === 0 && (
          <div style={{ padding: "16px 20px", background: T.warnBg, border: `1px solid ${T.warnBorder}`, marginBottom: 28, display: "flex", justifyContent: "space-between", alignItems: "center", animation: "fadeIn 0.3s ease" }}>
            <span style={{ fontSize: 13, color: T.warnText, fontFamily: "monospace" }}>⚠ No API keys configured — add at least 2 to get started</span>
            <button onClick={() => setShowModal(true)} style={{ padding: "7px 16px", background: "#D97B4A", border: "none", color: "#0a0a0a", fontSize: 12, fontFamily: "monospace", letterSpacing: "0.1em", cursor: "pointer" }}>Add Keys →</button>
          </div>
        )}

        {/* Active model pills */}
        {configuredCount > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
            {MODELS.filter((m) => keys[m.id]?.trim()).map((m) => {
              const vDef = m.versions.find((v) => v.id === selectedVersions[m.id]);
              return (
                <button key={m.id} onClick={() => setShowModal(true)} title="Click to change model"
                  style={{ display: "flex", alignItems: "center", gap: 5, padding: "4px 10px", background: m.color + "12", border: `1px solid ${m.color}33`, color: m.color, fontSize: 11, fontFamily: "monospace", cursor: "pointer", letterSpacing: "0.05em", transition: "all 0.15s" }}
                  onMouseEnter={(e) => e.currentTarget.style.background = m.color + "22"}
                  onMouseLeave={(e) => e.currentTarget.style.background = m.color + "12"}>
                  <span>{m.icon}</span>
                  <span>{m.label}</span>
                  {vDef && <span style={{ opacity: 0.6, fontSize: 9 }}>· {vDef.label}</span>}
                </button>
              );
            })}
          </div>
        )}

        {/* Usage & Cost Tracker */}
        <UsageTrackerPanel
          usageLog={usageLog}
          pricing={pricing}
          pricingTs={pricingTs}
          pricingLoading={pricingLoading}
          onRefreshPricing={handleRefreshPricing}
          onResetPricing={handleResetPricing}
          hasClaudeKey={!!keys["claude"]?.trim()}
          T={T}
          sessionStartTs={sessionStartTs}
          onClear={() => {
            setUsageLog([]); saveUsageLog([]);
            setSessionTotals({}); saveSessionTotals({});
          }}
          onExportJSON={() => downloadJSON({
            exportedAt: new Date().toISOString(),
            pricingLastUpdated: pricingTs ? new Date(pricingTs).toISOString() : null,
            sessionTotals,
            usageLog,
            history,
          }, `factcheck-data-${Date.now()}.json`)}
        />

        {/* Feature 4: Persona Selector */}
        <PersonaSelector selected={selectedPersona} onSelect={handlePersonaChange} T={T} />

        {/* Disclaimer */}
        <DisclaimerBanner T={T} />

        {/* Prompt */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 10, letterSpacing: "0.2em", color: T.textGhost, textTransform: "uppercase", fontFamily: "monospace", marginBottom: 10 }}>Your question or claim to fact-check</div>
          <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleQuery(); }}
            placeholder="e.g. Is it true that humans only use 10% of their brain?" rows={3}
            style={{ width: "100%", background: T.bgInput, border: `1px solid ${T.borderMid}`, borderBottom: `2px solid ${T.borderHi}`, color: T.text, padding: "16px 20px", fontSize: 15, lineHeight: 1.6, resize: "vertical", fontFamily: "Georgia, serif", boxSizing: "border-box", overflow: "auto" }} />
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
            <span style={{ fontSize: 11, fontFamily: "monospace", color: T.textDeep }}>⌘ + Enter to run</span>
            {currentPersona.id !== "balanced" && (
              <span style={{ fontSize: 11, fontFamily: "monospace", color: currentPersona.color }}>
                {currentPersona.icon} {currentPersona.label} mode active
              </span>
            )}
          </div>
        </div>

        {/* Run button */}
        <button onClick={handleQuery} disabled={!prompt.trim() || anyRunning || configuredCount < 2}
          style={{ width: "100%", padding: "15px", background: !prompt.trim() || configuredCount < 2 ? T.border : anyRunning ? T.border : T.accent, color: !prompt.trim() || configuredCount < 2 ? T.textGhost : anyRunning ? T.textFaint : T.accentText, border: "none", fontSize: 13, letterSpacing: "0.18em", textTransform: "uppercase", fontFamily: "monospace", cursor: !prompt.trim() || anyRunning || configuredCount < 2 ? "not-allowed" : "pointer", transition: "all 0.2s", marginBottom: 28 }}>
          {anyRunning ? "◈ Streaming all AIs in parallel..." : "◈ Ask All AIs & Fact-Check"}
        </button>

        {Object.keys(statuses).length > 0 && <ProgressBar statuses={statuses} T={T} />}

        {/* Feature 1: Re-analyze banner */}
        {needsReanalysis && !anyRunning && !analyzing && (
          <div style={{ padding: "12px 18px", background: "#D97B4A10", border: `1px solid #D97B4A33`, marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center", animation: "fadeIn 0.3s ease" }}>
            <span style={{ fontSize: 12, fontFamily: "monospace", color: "#D97B4A" }}>◈ Model response updated — re-run analysis with new data?</span>
            <button onClick={handleReanalyze} style={{ padding: "6px 14px", background: "#D97B4A", border: "none", color: "#0a0a0a", fontSize: 11, fontFamily: "monospace", cursor: "pointer", letterSpacing: "0.1em" }}>
              Re-analyze →
            </button>
          </div>
        )}

        {/* Confidence + Export bar */}
        {hasReport && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", background: T.bgElevated, border: `1px solid ${T.border}`, marginBottom: 16, animation: "fadeIn 0.4s ease" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 11, fontFamily: "monospace", color: T.textFaint, letterSpacing: "0.12em", textTransform: "uppercase" }}>Verdict</span>
              {confidenceLevel && <ConfidenceMeter level={confidenceLevel} />}
              {currentPersona.id !== "balanced" && (
                <span style={{ fontSize: 10, fontFamily: "monospace", color: currentPersona.color, background: currentPersona.color + "12", padding: "2px 8px", border: `1px solid ${currentPersona.color}33` }}>
                  {currentPersona.icon} {currentPersona.label}
                </span>
              )}
            </div>
            <button onClick={() => setShowReport(true)}
              style={{ padding: "9px 20px", background: T.accent, border: "none", color: T.accentText, fontSize: 12, fontFamily: "monospace", cursor: "pointer", letterSpacing: "0.14em", textTransform: "uppercase", display: "flex", alignItems: "center", gap: 8 }}>
              <span>⊞</span> Export Report
            </button>
          </div>
        )}

        {/* Response cards */}
        {Object.keys(statuses).length > 0 && (
          <div style={{ animation: "fadeIn 0.3s ease" }}>
            <div
              onClick={() => setResponsesOpen(o => !o)}
              style={{ fontSize: 10, letterSpacing: "0.2em", color: T.textGhost, textTransform: "uppercase", fontFamily: "monospace", marginBottom: responsesOpen ? 12 : 0, display: "flex", alignItems: "center", gap: 12, cursor: "pointer", userSelect: "none" }}>
              <span>AI Responses</span>
              {anyRunning && <div style={{ fontSize: 9, color: T.textGhost, fontFamily: "monospace" }}>· streaming live</div>}
              {!anyRunning && Object.values(statuses).filter(s => s === "done").length > 0 && (
                <div style={{ fontSize: 9, color: T.textGhost, fontFamily: "monospace" }}>
                  · {Object.values(statuses).filter(s => s === "done").length} done
                </div>
              )}
              <div style={{ flex: 1, height: 1, background: T.border }} />
              <span style={{ fontSize: 10, color: T.textGhost }}>{responsesOpen ? "▲" : "▼"}</span>
            </div>
            {responsesOpen && (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 2, marginBottom: 28 }}>
                {MODELS.map((m) => (
                  <ModelResponseCard
                    key={m.id}
                    model={m}
                    status={statuses[m.id] || "idle"}
                    response={responses[m.id]}
                    error={errors[m.id]}
                    alignment={alignment}
                    selectedVersion={selectedVersions[m.id]}
                    onRetry={prompt.trim() ? handleRetry : null}
                    usage={sessionTotals[m.id] && sessionTotals[m.id].calls > 0 ? {
                      inputTokens: usageLog.filter(e => e.modelId === m.id).slice(-1)[0]?.inputTokens || 0,
                      outputTokens: usageLog.filter(e => e.modelId === m.id).slice(-1)[0]?.outputTokens || 0,
                      cost: usageLog.filter(e => e.modelId === m.id).slice(-1)[0]?.cost || 0,
                    } : null}
                    T={T}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Analysis section */}
        {(analyzing || analysis) && (
          <div style={{ animation: "fadeIn 0.4s ease" }}>
            <div style={{ fontSize: 10, letterSpacing: "0.2em", color: T.textGhost, textTransform: "uppercase", fontFamily: "monospace", marginBottom: 12, display: "flex", alignItems: "center", gap: 12 }}>
              <span>Fact-Check Report</span>
              {Object.values(annotations).some((a) => Object.keys(a).length > 0) && (
                <span style={{ fontSize: 9, fontFamily: "monospace", color: "#E8C84A" }}>
                  · {Object.values(annotations).reduce((s, a) => s + Object.keys(a).length, 0)} annotated
                </span>
              )}
              <div style={{ flex: 1, height: 1, background: T.border }} />
            </div>

            {analyzing && !analysis && (
              <div style={{ padding: "36px", background: T.bgElevated, border: `1px solid ${T.border}`, textAlign: "center" }}>
                <div style={{ fontSize: 24, marginBottom: 12, color: "#D97B4A", opacity: 0.6 }}>◈</div>
                <div style={{ fontSize: 12, fontFamily: "monospace", color: T.textFaint, letterSpacing: "0.1em" }}>Synthesizing responses...</div>
              </div>
            )}

            {/* Feature 2: Annotatable sections */}
            {sections && sections.map((sec) => (
              <div key={sec.key} style={{ background: T.bgElevated, border: `1px solid ${T.border}`, borderLeft: `3px solid ${sec.color}`, marginBottom: 2, animation: "fadeIn 0.4s ease" }}>
                <div style={{ padding: "12px 18px", display: "flex", alignItems: "center", gap: 8, borderBottom: `1px solid ${T.border}` }}>
                  <span style={{ color: sec.color }}>{sec.emoji}</span>
                  <span style={{ fontSize: 11, letterSpacing: "0.15em", textTransform: "uppercase", fontFamily: "monospace", color: sec.color }}>{sec.label}</span>
                  {ANNOTATABLE_SECTIONS.has(sec.key) && (
                    <span style={{ fontSize: 9, fontFamily: "monospace", color: T.textGhost, marginLeft: 4 }}>· click claims to annotate</span>
                  )}
                  {annotations[sec.key] && Object.keys(annotations[sec.key]).length > 0 && (
                    <span style={{ fontSize: 9, fontFamily: "monospace", color: "#E8C84A", background: "#1a1700", padding: "1px 5px", border: "1px solid #E8C84A33" }}>
                      {Object.keys(annotations[sec.key]).length} annotated
                    </span>
                  )}
                </div>
                <AnnotatedSection
                  section={sec}
                  annotations={annotations[sec.key] || {}}
                  onAnnotate={handleAnnotate}
                  T={T}
                />
              </div>
            ))}

            {analysis && !sections && (
              <div style={{ padding: "20px", background: T.bgElevated, border: `1px solid ${T.border}`, fontSize: 14, lineHeight: 1.8, color: T.textMuted, whiteSpace: "pre-wrap", fontFamily: "Georgia, serif" }}>{analysis}</div>
            )}

            <LiveSourcesPanel sources={sources} searching={searching} T={T} />
          </div>
        )}

        {/* Footer */}
        <div style={{ marginTop: 48, paddingTop: 20, borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <span style={{ fontSize: 11, fontFamily: "monospace", color: T.textGhost, letterSpacing: "0.05em" }}>
              Munki Business · Multi AI Fact-Check Engine
            </span>
            <a href="https://anaken.one/" target="_blank" rel="noopener noreferrer"
              style={{ fontSize: 11, fontFamily: "monospace", color: T.textFaint, letterSpacing: "0.1em", textDecoration: "none", borderBottom: `1px solid ${T.borderMid}`, paddingBottom: 1, transition: "color 0.2s" }}
              onMouseEnter={e => e.target.style.color = T.textMuted}
              onMouseLeave={e => e.target.style.color = T.textFaint}>
              ✦ anaken.one
            </a>
          </div>
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <button onClick={() => setShowTutorial(true)}
              style={{ background: "none", border: "none", fontSize: 11, fontFamily: "monospace", color: T.textGhost, cursor: "pointer", letterSpacing: "0.1em", padding: 0 }}>
              Tutorial
            </button>
            <button onClick={() => setShowChangelog(true)}
              style={{ background: "none", border: "none", fontSize: 11, fontFamily: "monospace", color: T.textGhost, cursor: "pointer", letterSpacing: "0.1em", display: "flex", alignItems: "center", gap: 6, padding: 0 }}>
              <span style={{ fontSize: 9, padding: "1px 6px", border: `1px solid ${T.borderMid}`, color: T.textGhost }}>v8.0</span>
              <span>Changelog →</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
